import "@dxos/node-std/globals"

// packages/sdk/client-services/src/packlets/devtools/feeds.ts
import { EventSubscriptions } from "@dxos/async";
import { Stream } from "@dxos/codec-protobuf";
import { FeedIterator } from "@dxos/feed-store";
import { PublicKey } from "@dxos/keys";
import { ComplexMap } from "@dxos/util";
var subscribeToFeeds = ({ feedStore }, { feedKeys }) => {
  return new Stream(({ next }) => {
    if ((feedKeys == null ? void 0 : feedKeys.length) === 0) {
      return;
    }
    const subscriptions = new EventSubscriptions();
    const feedMap = new ComplexMap(PublicKey.hash);
    const update = () => {
      const { feeds } = feedStore;
      feeds.filter((feed) => !(feedKeys == null ? void 0 : feedKeys.length) || feedKeys.some((feedKey) => feedKey.equals(feed.key))).forEach((feed) => {
        if (!feedMap.has(feed.key)) {
          feedMap.set(feed.key, feed);
          feed.on("close", update);
          subscriptions.add(feed.off("close", update));
        }
      });
      next({
        feeds: Array.from(feedMap.values()).map((feed) => ({
          feedKey: feed.key,
          length: feed.properties.length
        }))
      });
    };
    subscriptions.add(feedStore.feedOpened.on(update));
    update();
    return () => {
      subscriptions.clear();
    };
  });
};
var subscribeToFeedBlocks = ({ feedStore }, { feedKey, maxBlocks = 10 }) => {
  return new Stream(({ next }) => {
    if (!feedKey) {
      return;
    }
    const subscriptions = new EventSubscriptions();
    const timeout = setTimeout(async () => {
      const feed = feedStore.getFeed(feedKey);
      if (!feed) {
        return;
      }
      const update = async () => {
        const iterator = new FeedIterator(feed);
        await iterator.open();
        const blocks = [];
        for await (const block of iterator) {
          blocks.push(block);
          if (blocks.length >= feed.properties.length) {
            break;
          }
        }
        next({
          blocks: blocks.slice(-maxBlocks)
        });
        await iterator.close();
      };
      feed.on("append", update);
      subscriptions.add(() => feed.off("append", update));
      feed.on("truncate", update);
      subscriptions.add(() => feed.off("truncate", update));
      await update();
    });
    return () => {
      subscriptions.clear();
      clearTimeout(timeout);
    };
  });
};

// packages/sdk/client-services/src/packlets/devtools/network.ts
import { Stream as Stream2 } from "@dxos/codec-protobuf";
import { Context } from "@dxos/context";
import { PublicKey as PublicKey2 } from "@dxos/keys";
var subscribeToNetworkStatus = ({ networkManager }) => new Stream2(({ next, close }) => {
  const update = () => {
    try {
      const status = networkManager.signalManager.getStatus();
      next({
        servers: status
      });
    } catch (err) {
      close(err);
    }
  };
  networkManager.signalManager.statusChanged.on(update);
  update();
});
var subscribeToSignal = ({ networkManager }) => new Stream2(({ next }) => {
  const ctx = new Context();
  networkManager.signalManager.onMessage.on(ctx, (message) => {
    next({
      message: {
        author: message.author.asUint8Array(),
        recipient: message.recipient.asUint8Array(),
        payload: message.payload
      },
      receivedAt: new Date()
    });
  });
  networkManager.signalManager.swarmEvent.on(ctx, (swarmEvent) => {
    next({
      swarmEvent: swarmEvent.swarmEvent,
      receivedAt: new Date()
    });
  });
  return () => {
    return ctx.dispose();
  };
});
var subscribeToNetworkTopics = ({ networkManager }) => new Stream2(({ next, close }) => {
  const update = () => {
    try {
      const topics = networkManager.topics;
      const labeledTopics = topics.map((topic) => {
        var _a, _b;
        return {
          topic,
          label: (_b = (_a = networkManager.getSwarm(topic)) == null ? void 0 : _a.label) != null ? _b : topic.toHex()
        };
      });
      next({
        topics: labeledTopics
      });
    } catch (err) {
      close(err);
    }
  };
  networkManager.topicsUpdated.on(update);
  update();
});
var subscribeToSwarmInfo = ({ networkManager }) => new Stream2(({ next }) => {
  var _a;
  const update = () => {
    var _a2;
    const info = (_a2 = networkManager.connectionLog) == null ? void 0 : _a2.swarms;
    if (info) {
      next({
        data: info
      });
    }
  };
  (_a = networkManager.connectionLog) == null ? void 0 : _a.update.on(update);
  update();
});
var getNetworkPeers = ({ networkManager }, request) => {
  if (!request.topic) {
    throw new Error("Expected a network topic");
  }
  const map = networkManager.getSwarmMap(PublicKey2.from(request.topic));
  return {
    peers: map == null ? void 0 : map.peers.map((peer) => ({
      ...peer,
      connections: peer.connections.map((connection) => connection.asUint8Array())
    }))
  };
};

// packages/sdk/client-services/src/packlets/devtools/spaces.ts
import { Stream as Stream3 } from "@dxos/codec-protobuf";
var subscribeToSpaces = (context, { spaceKeys = [] }) => {
  return new Stream3(({ next }) => {
    let unsubscribe;
    const update = async () => {
      const spaces = [
        ...context.spaceManager.spaces.values()
      ];
      const filteredSpaces = spaces.filter((space) => !(spaceKeys == null ? void 0 : spaceKeys.length) || spaceKeys.some((spaceKey) => spaceKey.equals(space.key)));
      next({
        spaces: filteredSpaces.map((space) => {
          const spaceMetadata = context.metadataStore.spaces.find((spaceMetadata2) => spaceMetadata2.key.equals(space.key));
          return {
            key: space.key,
            isOpen: space.isOpen,
            timeframe: spaceMetadata == null ? void 0 : spaceMetadata.dataTimeframe,
            genesisFeed: space.genesisFeedKey,
            controlFeed: space.controlFeedKey,
            dataFeed: space.dataFeedKey
          };
        })
      });
    };
    const timeout = setTimeout(async () => {
      await context.initialized.wait();
      unsubscribe = context.dataSpaceManager.updated.on(() => update());
      await update();
    });
    return () => {
      unsubscribe == null ? void 0 : unsubscribe();
      clearTimeout(timeout);
    };
  });
};

// packages/sdk/client-services/src/packlets/devtools/devtools.ts
import { Event as AsyncEvent } from "@dxos/async";
import { Stream as Stream5 } from "@dxos/codec-protobuf";

// packages/sdk/client-services/src/packlets/devtools/keys.ts
import { Stream as Stream4 } from "@dxos/codec-protobuf";
var subscribeToKeyringKeys = ({ keyring }) => new Stream4(({ next }) => {
  const update = () => {
    next({
      keys: keyring.list()
    });
  };
  const unsubscribe = keyring.keysUpdate.on(update);
  update();
  return unsubscribe;
});

// packages/sdk/client-services/src/packlets/devtools/devtools.ts
var DevtoolsHostEvents = class {
  constructor() {
    this.ready = new AsyncEvent();
  }
};
var DevtoolsServiceImpl = class {
  constructor(params) {
    this.params = params;
  }
  events(request) {
    return new Stream5(({ next }) => {
      this.params.events.ready.on(() => {
        next({
          ready: {}
        });
      });
    });
  }
  getConfig(request) {
    throw new Error();
  }
  resetStorage(request) {
    throw new Error();
  }
  enableDebugLogging(request) {
    throw new Error();
  }
  disableDebugLogging(request) {
    throw new Error();
  }
  subscribeToKeyringKeys(request) {
    return subscribeToKeyringKeys({
      keyring: this.params.context.keyring
    });
  }
  subscribeToCredentialMessages(request) {
    throw new Error();
  }
  subscribeToSpaces(request) {
    return subscribeToSpaces(this.params.context, request);
  }
  subscribeToItems(request) {
    throw new Error();
  }
  subscribeToFeeds(request) {
    return subscribeToFeeds({
      feedStore: this.params.context.feedStore
    }, request);
  }
  subscribeToFeedBlocks(request) {
    return subscribeToFeedBlocks({
      feedStore: this.params.context.feedStore
    }, request);
  }
  getSpaceSnapshot(request) {
    throw new Error();
  }
  saveSpaceSnapshot(request) {
    throw new Error();
  }
  clearSnapshots(request) {
    throw new Error();
  }
  getNetworkPeers(request) {
    throw new Error();
  }
  subscribeToNetworkTopics(request) {
    throw new Error();
  }
  subscribeToSignalStatus(request) {
    return subscribeToNetworkStatus({
      networkManager: this.params.context.networkManager
    });
  }
  subscribeToSignal() {
    return subscribeToSignal({
      networkManager: this.params.context.networkManager
    });
  }
  subscribeToSwarmInfo() {
    return subscribeToSwarmInfo({
      networkManager: this.params.context.networkManager
    });
  }
};

// packages/sdk/client-services/src/packlets/identity/authenticator.ts
import { Trigger } from "@dxos/async";
import { Context as Context2 } from "@dxos/context";
import { verifyCredential } from "@dxos/credentials";
import { log } from "@dxos/log";
import { schema } from "@dxos/protocols";
var createAuthProvider = (signer) => async (nonce) => {
  const credential = await signer.createCredential({
    assertion: {
      "@type": "dxos.halo.credentials.Auth"
    },
    subject: signer.getIssuer(),
    nonce
  });
  return schema.getCodecForType("dxos.halo.credentials.Credential").encode(credential);
};
var TrustedKeySetAuthVerifier = class {
  // prettier-ignore
  constructor(_params) {
    this._params = _params;
    this._ctx = new Context2();
  }
  async close() {
    await this._ctx.dispose();
  }
  get verifier() {
    return async (nonce, auth) => {
      const credential = schema.getCodecForType("dxos.halo.credentials.Credential").decode(auth);
      log("authenticating...", {
        credential
      }, {
        file: "authenticator.ts",
        line: 57,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      const result = await verifyCredential(credential);
      if (result.kind === "fail") {
        log("Invalid credential", {
          result
        }, {
          file: "authenticator.ts",
          line: 61,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return false;
      }
      if (!credential.proof.nonce || !Buffer.from(nonce).equals(credential.proof.nonce)) {
        log("Invalid nonce", {
          nonce,
          credential
        }, {
          file: "authenticator.ts",
          line: 66,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return false;
      }
      if (this._isTrustedKey(credential.issuer)) {
        log("key is not currently in trusted set, waiting...", {
          key: credential.issuer
        }, {
          file: "authenticator.ts",
          line: 71,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return true;
      }
      const trigger = new Trigger();
      this._ctx.onDispose(() => {
        trigger.wake(false);
      });
      const clear = this._params.update.on(this._ctx, () => {
        if (this._isTrustedKey(credential.issuer)) {
          log("auth success", {
            key: credential.issuer
          }, {
            file: "authenticator.ts",
            line: 82,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          trigger.wake(true);
        } else {
          log("key is not currently in trusted set, waiting...", {
            key: credential.issuer
          }, {
            file: "authenticator.ts",
            line: 85,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
      });
      try {
        return await trigger.wait({
          timeout: this._params.authTimeout
        });
      } catch (e) {
        return false;
      } finally {
        clear();
      }
    };
  }
  _isTrustedKey(deviceKey) {
    const deviceSet = this._params.trustedKeysProvider();
    return deviceSet.has(deviceKey);
  }
};

// packages/sdk/client-services/src/packlets/identity/identity.ts
import assert from "@dxos/node-std/assert";
import { Event } from "@dxos/async";
import { DeviceStateMachine, createCredentialSignerWithKey, createCredentialSignerWithChain, ProfileStateMachine } from "@dxos/credentials";
import { failUndefined } from "@dxos/debug";
import { writeMessages } from "@dxos/feed-store";
import { log as log2 } from "@dxos/log";
import { AdmittedFeed } from "@dxos/protocols/proto/dxos/halo/credentials";
var AUTH_TIMEOUT = 3e4;
var Identity = class {
  constructor({ space, signer, identityKey, deviceKey }) {
    this.stateUpdate = new Event();
    this.space = space;
    this._signer = signer;
    this.identityKey = identityKey;
    this.deviceKey = deviceKey;
    this._deviceStateMachine = this.space.spaceState.registerProcessor(new DeviceStateMachine({
      identityKey: this.identityKey,
      deviceKey: this.deviceKey,
      onUpdate: () => this.stateUpdate.emit()
    }));
    this._profileStateMachine = this.space.spaceState.registerProcessor(new ProfileStateMachine({
      identityKey: this.identityKey,
      onUpdate: () => this.stateUpdate.emit()
    }));
    this.authVerifier = new TrustedKeySetAuthVerifier({
      trustedKeysProvider: () => this.authorizedDeviceKeys,
      update: this.stateUpdate,
      authTimeout: AUTH_TIMEOUT
    });
  }
  // TODO(burdon): Expose state object?
  get authorizedDeviceKeys() {
    return this._deviceStateMachine.processor.authorizedDeviceKeys;
  }
  async open() {
    await this._deviceStateMachine.open();
    await this._profileStateMachine.open();
    await this.space.open();
  }
  async close() {
    await this.authVerifier.close();
    await this._deviceStateMachine.close();
    await this._profileStateMachine.close();
    await this.space.close();
  }
  async ready() {
    await this._deviceStateMachine.processor.deviceChainReady.wait();
    await this.controlPipeline.state.waitUntilReachedTargetTimeframe({
      timeout: 3e3
    });
  }
  get profileDocument() {
    return this._profileStateMachine.processor.profile;
  }
  /**
  * @test-only
  */
  get controlPipeline() {
    return this.space.controlPipeline;
  }
  get haloSpaceKey() {
    return this.space.key;
  }
  get haloGenesisFeedKey() {
    return this.space.genesisFeedKey;
  }
  get deviceCredentialChain() {
    return this._deviceStateMachine.processor.deviceCredentialChain;
  }
  getAdmissionCredentials() {
    var _a, _b;
    return {
      deviceKey: this.deviceKey,
      controlFeedKey: (_a = this.space.controlFeedKey) != null ? _a : failUndefined(),
      dataFeedKey: (_b = this.space.dataFeedKey) != null ? _b : failUndefined()
    };
  }
  /**
  * Issues credentials as identity.
  * Requires identity to be ready.
  */
  getIdentityCredentialSigner() {
    assert(this._deviceStateMachine.processor.deviceCredentialChain, "Device credential chain is not ready.");
    return createCredentialSignerWithChain(this._signer, this._deviceStateMachine.processor.deviceCredentialChain, this.deviceKey);
  }
  /**
  * Issues credentials as device.
  */
  getDeviceCredentialSigner() {
    return createCredentialSignerWithKey(this._signer, this.deviceKey);
  }
  async admitDevice({ deviceKey, controlFeedKey, dataFeedKey }) {
    log2("Admitting device:", {
      identityKey: this.identityKey,
      hostDevice: this.deviceKey,
      deviceKey,
      controlFeedKey,
      dataFeedKey
    }, {
      file: "identity.ts",
      line: 160,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const signer = this.getIdentityCredentialSigner();
    await writeMessages(this.controlPipeline.writer, [
      await signer.createCredential({
        subject: deviceKey,
        assertion: {
          "@type": "dxos.halo.credentials.AuthorizedDevice",
          identityKey: this.identityKey,
          deviceKey
        }
      }),
      await signer.createCredential({
        subject: controlFeedKey,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          spaceKey: this.haloSpaceKey,
          deviceKey,
          identityKey: this.identityKey,
          designation: AdmittedFeed.Designation.CONTROL
        }
      }),
      await signer.createCredential({
        subject: dataFeedKey,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          spaceKey: this.haloSpaceKey,
          deviceKey,
          identityKey: this.identityKey,
          designation: AdmittedFeed.Designation.DATA
        }
      })
    ].map((credential) => ({
      credential: {
        credential
      }
    })));
  }
};

// packages/sdk/client-services/src/packlets/identity/identity-manager.ts
import assert2 from "@dxos/node-std/assert";
import { Event as Event2 } from "@dxos/async";
import { createCredentialSignerWithKey as createCredentialSignerWithKey2, CredentialGenerator } from "@dxos/credentials";
import { PublicKey as PublicKey3 } from "@dxos/keys";
import { log as log3 } from "@dxos/log";
import { trace } from "@dxos/protocols";
import { AdmittedFeed as AdmittedFeed2 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { deferFunction } from "@dxos/util";
var IdentityManager = class {
  // TODO(burdon): IdentityManagerParams.
  // TODO(dmaretskyi): Perhaps this should take/generate the peerKey outside of an initialized identity.
  constructor(_metadataStore, _keyring, _feedStore, _spaceManager) {
    this._metadataStore = _metadataStore;
    this._keyring = _keyring;
    this._feedStore = _feedStore;
    this._spaceManager = _spaceManager;
    this.stateUpdate = new Event2();
    this._instanceId = PublicKey3.random().toHex();
  }
  get identity() {
    return this._identity;
  }
  async open() {
    var _a;
    log3.trace("dxos.halo.identity-manager", trace.begin({
      id: this._instanceId,
      parentId: this._traceParent
    }), {
      file: "identity-manager.ts",
      line: 72,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._metadataStore.load();
    const identityRecord = this._metadataStore.getIdentityRecord();
    log3("identity record", {
      identityRecord
    }, {
      file: "identity-manager.ts",
      line: 76,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (identityRecord) {
      this._identity = await this._constructIdentity(identityRecord);
      await this._identity.open();
      await this._identity.ready();
      log3.trace("dxos.halo.identity", {
        identityKey: identityRecord.identityKey,
        displayName: (_a = this._identity.profileDocument) == null ? void 0 : _a.displayName
      }, {
        file: "identity-manager.ts",
        line: 81,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      this.stateUpdate.emit();
    }
  }
  async close() {
    var _a;
    await ((_a = this._identity) == null ? void 0 : _a.close());
    log3.trace("dxos.halo.identity-manager", trace.end({
      id: this._instanceId
    }), {
      file: "identity-manager.ts",
      line: 91,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  async createIdentity({ displayName } = {}) {
    var _a;
    assert2(!this._identity, "Identity already exists.");
    log3("creating identity...", {}, {
      file: "identity-manager.ts",
      line: 96,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const controlFeedKey = await this._keyring.createKey();
    const identityRecord = {
      identityKey: await this._keyring.createKey(),
      deviceKey: await this._keyring.createKey(),
      haloSpace: {
        key: await this._keyring.createKey(),
        genesisFeedKey: controlFeedKey,
        controlFeedKey,
        dataFeedKey: await this._keyring.createKey()
      }
    };
    const identity = await this._constructIdentity(identityRecord);
    await identity.open();
    {
      const generator = new CredentialGenerator(this._keyring, identityRecord.identityKey, identityRecord.deviceKey);
      assert2(identityRecord.haloSpace.genesisFeedKey, "Genesis feed key is required.");
      assert2(identityRecord.haloSpace.dataFeedKey, "Data feed key is required.");
      const credentials = [
        // Space genesis.
        ...await generator.createSpaceGenesis(identityRecord.haloSpace.key, identityRecord.haloSpace.genesisFeedKey),
        // Feed admission.
        await generator.createFeedAdmission(identityRecord.haloSpace.key, identityRecord.haloSpace.dataFeedKey, AdmittedFeed2.Designation.DATA)
      ];
      if (displayName) {
        credentials.push(await generator.createProfileCredential({
          displayName
        }));
      }
      credentials.push(await generator.createDeviceAuthorization(identityRecord.deviceKey));
      for (const credential of credentials) {
        await identity.controlPipeline.writer.write({
          credential: {
            credential
          }
        });
      }
    }
    await this._metadataStore.setIdentityRecord(identityRecord);
    this._identity = identity;
    await this._identity.ready();
    log3.trace("dxos.halo.identity", {
      identityKey: identityRecord.identityKey,
      displayName: (_a = this._identity.profileDocument) == null ? void 0 : _a.displayName
    }, {
      file: "identity-manager.ts",
      line: 151,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this.stateUpdate.emit();
    log3("created identity", {
      identityKey: identity.identityKey,
      deviceKey: identity.deviceKey
    }, {
      file: "identity-manager.ts",
      line: 157,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    return identity;
  }
  /**
  * Accept an existing identity. Expects it's device key to be authorized (now or later).
  */
  async acceptIdentity(params) {
    var _a;
    log3("accepting identity", {
      params
    }, {
      file: "identity-manager.ts",
      line: 165,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert2(!this._identity, "Identity already exists.");
    const identityRecord = {
      identityKey: params.identityKey,
      deviceKey: params.deviceKey,
      haloSpace: {
        key: params.haloSpaceKey,
        genesisFeedKey: params.haloGenesisFeedKey,
        controlFeedKey: params.controlFeedKey,
        dataFeedKey: params.dataFeedKey,
        controlTimeframe: params.controlTimeframe
      }
    };
    const identity = await this._constructIdentity(identityRecord);
    await identity.open();
    this._identity = identity;
    await this._metadataStore.setIdentityRecord(identityRecord);
    await this._identity.ready();
    log3.trace("dxos.halo.identity", {
      identityKey: identityRecord.identityKey,
      displayName: (_a = this._identity.profileDocument) == null ? void 0 : _a.displayName
    }, {
      file: "identity-manager.ts",
      line: 185,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this.stateUpdate.emit();
    log3("accepted identity", {
      identityKey: identity.identityKey,
      deviceKey: identity.deviceKey
    }, {
      file: "identity-manager.ts",
      line: 191,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    return identity;
  }
  async _constructIdentity(identityRecord) {
    assert2(!this._identity);
    log3("constructing identity", {
      identityRecord
    }, {
      file: "identity-manager.ts",
      line: 197,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert2(identityRecord.haloSpace.controlFeedKey);
    const controlFeed = await this._feedStore.openFeed(identityRecord.haloSpace.controlFeedKey, {
      writable: true
    });
    assert2(identityRecord.haloSpace.dataFeedKey);
    const dataFeed = await this._feedStore.openFeed(identityRecord.haloSpace.dataFeedKey, {
      writable: true
    });
    const space = await this._constructSpace({
      spaceRecord: identityRecord.haloSpace,
      swarmIdentity: {
        peerKey: identityRecord.deviceKey,
        credentialProvider: createAuthProvider(createCredentialSignerWithKey2(this._keyring, identityRecord.deviceKey)),
        credentialAuthenticator: deferFunction(() => identity.authVerifier.verifier)
      },
      identityKey: identityRecord.identityKey
    });
    space.setControlFeed(controlFeed);
    space.setDataFeed(dataFeed);
    const identity = new Identity({
      space,
      signer: this._keyring,
      identityKey: identityRecord.identityKey,
      deviceKey: identityRecord.deviceKey
    });
    log3("done", {
      identityKey: identityRecord.identityKey
    }, {
      file: "identity-manager.ts",
      line: 225,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (identityRecord.haloSpace.controlTimeframe) {
      identity.controlPipeline.state.setTargetTimeframe(identityRecord.haloSpace.controlTimeframe);
    }
    return identity;
  }
  async _constructSpace({ spaceRecord, swarmIdentity, identityKey }) {
    return this._spaceManager.constructSpace({
      metadata: {
        key: spaceRecord.key,
        genesisFeedKey: spaceRecord.genesisFeedKey
      },
      swarmIdentity,
      onNetworkConnection: () => {
      }
    });
  }
};

// packages/sdk/client-services/src/packlets/identity/identity-service.ts
import assert3 from "@dxos/node-std/assert";
import { Stream as Stream6 } from "@dxos/codec-protobuf";
import { signPresentation } from "@dxos/credentials";
import { todo } from "@dxos/debug";
var IdentityServiceImpl = class {
  // TODO(wittjosiah): Remove dependency on service context.
  constructor(_serviceContext) {
    this._serviceContext = _serviceContext;
  }
  async createIdentity(request) {
    await this._serviceContext.createIdentity(request);
    return this._getIdentity();
  }
  async recoverIdentity(request) {
    return todo();
  }
  queryIdentity() {
    return new Stream6(({ next }) => {
      const emitNext = () => next({
        identity: this._getIdentity()
      });
      emitNext();
      return this._serviceContext.identityManager.stateUpdate.on(emitNext);
    });
  }
  _getIdentity() {
    if (!this._serviceContext.identityManager.identity) {
      return void 0;
    }
    return {
      identityKey: this._serviceContext.identityManager.identity.identityKey,
      spaceKey: this._serviceContext.identityManager.identity.space.key,
      profile: this._serviceContext.identityManager.identity.profileDocument
    };
  }
  async signPresentation({ presentation, nonce }) {
    assert3(this._serviceContext.identityManager.identity, "Identity not initialized.");
    return await signPresentation({
      presentation,
      signer: this._serviceContext.keyring,
      signerKey: this._serviceContext.identityManager.identity.deviceKey,
      chain: this._serviceContext.identityManager.identity.deviceCredentialChain,
      nonce
    });
  }
};

// packages/sdk/client-services/src/packlets/invitations/device-invitation-protocol.ts
import assert4 from "@dxos/node-std/assert";
import { Invitation } from "@dxos/protocols/proto/dxos/client/services";
var DeviceInvitationProtocol = class {
  constructor(_keyring, _getIdentity, _acceptIdentity) {
    this._keyring = _keyring;
    this._getIdentity = _getIdentity;
    this._acceptIdentity = _acceptIdentity;
  }
  toJSON() {
    return {};
  }
  getInvitationContext() {
    return {
      kind: Invitation.Kind.DEVICE
    };
  }
  async admit(request) {
    assert4(request.device);
    const identity = this._getIdentity();
    await identity.admitDevice(request.device);
    return {
      device: {
        identityKey: identity.identityKey,
        haloSpaceKey: identity.haloSpaceKey,
        genesisFeedKey: identity.haloGenesisFeedKey,
        controlTimeframe: identity.controlPipeline.state.timeframe
      }
    };
  }
  createIntroduction() {
    return {};
  }
  async createAdmissionRequest() {
    const deviceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const dataFeedKey = await this._keyring.createKey();
    return {
      device: {
        deviceKey,
        controlFeedKey,
        dataFeedKey
      }
    };
  }
  async accept(response, request) {
    assert4(response.device);
    const { identityKey, haloSpaceKey, genesisFeedKey, controlTimeframe } = response.device;
    assert4(request.device);
    const { deviceKey, controlFeedKey, dataFeedKey } = request.device;
    await this._acceptIdentity({
      identityKey,
      deviceKey,
      haloSpaceKey,
      haloGenesisFeedKey: genesisFeedKey,
      controlFeedKey,
      dataFeedKey,
      controlTimeframe
    });
    return {
      identityKey
    };
  }
};

// packages/sdk/client-services/src/packlets/invitations/invitations-handler.ts
import assert5 from "@dxos/node-std/assert";
import { PushStream, scheduleTask, sleep, TimeoutError, Trigger as Trigger2 } from "@dxos/async";
import { AUTHENTICATION_CODE_LENGTH, CancellableInvitationObservable, INVITATION_TIMEOUT, ON_CLOSE_DELAY, AuthenticatingInvitationObservable } from "@dxos/client";
import { Context as Context3 } from "@dxos/context";
import { generatePasscode } from "@dxos/credentials";
import { PublicKey as PublicKey4 } from "@dxos/keys";
import { log as log4 } from "@dxos/log";
import { createTeleportProtocolFactory, StarTopology } from "@dxos/network-manager";
import { schema as schema2 } from "@dxos/protocols";
import { Invitation as Invitation2 } from "@dxos/protocols/proto/dxos/client/services";
import { AuthenticationResponse } from "@dxos/protocols/proto/dxos/halo/invitations";
import { RpcExtension } from "@dxos/teleport";
var MAX_OTP_ATTEMPTS = 3;
var InvitationsHandler = class {
  constructor(_networkManager) {
    this._networkManager = _networkManager;
  }
  createInvitation(protocol, options) {
    var _a;
    const { invitationId = PublicKey4.random().toHex(), type = Invitation2.Type.INTERACTIVE, authMethod = Invitation2.AuthMethod.SHARED_SECRET, state = Invitation2.State.INIT, timeout = INVITATION_TIMEOUT, swarmKey = PublicKey4.random() } = options != null ? options : {};
    const authCode = (_a = options == null ? void 0 : options.authCode) != null ? _a : authMethod === Invitation2.AuthMethod.SHARED_SECRET ? generatePasscode(AUTHENTICATION_CODE_LENGTH) : void 0;
    assert5(protocol);
    const invitation = {
      invitationId,
      type,
      authMethod,
      state,
      swarmKey,
      authCode,
      timeout,
      ...protocol.getInvitationContext()
    };
    const stream = new PushStream();
    const ctx = new Context3({
      onError: (err) => {
        void ctx.dispose();
        stream.error(err);
      }
    });
    ctx.onDispose(() => {
      log4("complete", {
        ...protocol.toJSON()
      }, {
        file: "invitations-handler.ts",
        line: 102,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      stream.complete();
    });
    const createExtension = () => {
      const success = new Trigger2();
      let guestProfile;
      let authenticationPassed = false;
      let authenticationRetry = 0;
      const extension = new InvitationHostExtension({
        introduce: async ({ profile }) => {
          log4("guest introduced itself", {
            guestProfile: profile,
            ...protocol.toJSON()
          }, {
            file: "invitations-handler.ts",
            line: 115,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          guestProfile = profile;
          stream.next({
            ...invitation,
            state: Invitation2.State.READY_FOR_AUTHENTICATION
          });
          return {
            spaceKey: authMethod === Invitation2.AuthMethod.NONE ? protocol.getInvitationContext().spaceKey : void 0,
            authMethod
          };
        },
        authenticate: async ({ authCode: code }) => {
          log4("received authentication request", {
            authCode: code
          }, {
            file: "invitations-handler.ts",
            line: 134,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          let status = AuthenticationResponse.Status.OK;
          switch (invitation.authMethod) {
            case Invitation2.AuthMethod.NONE: {
              log4("authentication not required", {}, {
                file: "invitations-handler.ts",
                line: 139,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              return {
                status: AuthenticationResponse.Status.OK
              };
            }
            case Invitation2.AuthMethod.SHARED_SECRET: {
              if (invitation.authCode) {
                if (authenticationRetry++ > MAX_OTP_ATTEMPTS) {
                  status = AuthenticationResponse.Status.INVALID_OPT_ATTEMPTS;
                } else if (code !== invitation.authCode) {
                  status = AuthenticationResponse.Status.INVALID_OTP;
                } else {
                  authenticationPassed = true;
                }
              }
              break;
            }
            default: {
              log4.error("invalid authentication method", {
                authMethod: invitation.authMethod
              }, {
                file: "invitations-handler.ts",
                line: 157,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              status = AuthenticationResponse.Status.INTERNAL_ERROR;
              break;
            }
          }
          return {
            status
          };
        },
        admit: async (admissionRequest) => {
          var _a2, _b, _c;
          try {
            if (isAuthenticationRequired(invitation) && !authenticationPassed) {
              throw new Error("Not authenticated");
            }
            const deviceKey = (_c = (_a2 = admissionRequest.device) == null ? void 0 : _a2.deviceKey) != null ? _c : (_b = admissionRequest.space) == null ? void 0 : _b.deviceKey;
            assert5(deviceKey);
            const admissionResponse = await protocol.admit(admissionRequest, guestProfile);
            success.wake(deviceKey);
            return admissionResponse;
          } catch (err) {
            stream.error(err);
            throw err;
          }
        },
        onOpen: () => {
          scheduleTask(ctx, async () => {
            try {
              log4("connected", {
                ...protocol.toJSON()
              }, {
                file: "invitations-handler.ts",
                line: 191,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              stream.next({
                ...invitation,
                state: Invitation2.State.CONNECTED
              });
              const deviceKey = await success.wait({
                timeout
              });
              log4("admitted guest", {
                guest: deviceKey,
                ...protocol.toJSON()
              }, {
                file: "invitations-handler.ts",
                line: 194,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              stream.next({
                ...invitation,
                state: Invitation2.State.SUCCESS
              });
            } catch (err) {
              if (err instanceof TimeoutError) {
                log4("timeout", {
                  ...protocol.toJSON()
                }, {
                  file: "invitations-handler.ts",
                  line: 198,
                  scope: this,
                  callSite: (f, a) => f(...a)
                });
                stream.next({
                  ...invitation,
                  state: Invitation2.State.TIMEOUT
                });
              } else {
                log4.error("failed", err, {
                  file: "invitations-handler.ts",
                  line: 201,
                  scope: this,
                  callSite: (f, a) => f(...a)
                });
                stream.error(err);
              }
            } finally {
              if (type !== Invitation2.Type.MULTIUSE) {
                await sleep(ON_CLOSE_DELAY);
                await ctx.dispose();
              }
            }
          });
        }
      });
      return extension;
    };
    scheduleTask(ctx, async () => {
      const topic = invitation.swarmKey;
      const swarmConnection = await this._networkManager.joinSwarm({
        topic,
        peerId: topic,
        protocolProvider: createTeleportProtocolFactory(async (teleport) => {
          teleport.addExtension("dxos.halo.invitations", createExtension());
        }),
        topology: new StarTopology(topic)
      });
      ctx.onDispose(() => swarmConnection.close());
      stream.next({
        ...invitation,
        state: Invitation2.State.CONNECTING
      });
    });
    const observable = new CancellableInvitationObservable({
      initialInvitation: invitation,
      subscriber: stream.observable,
      onCancel: async () => {
        stream.next({
          ...invitation,
          state: Invitation2.State.CANCELLED
        });
        await ctx.dispose();
      }
    });
    return observable;
  }
  acceptInvitation(protocol, invitation) {
    const { timeout = INVITATION_TIMEOUT } = invitation;
    assert5(protocol);
    const authenticated = new Trigger2();
    const stream = new PushStream();
    const ctx = new Context3({
      onError: (err) => {
        if (err instanceof TimeoutError) {
          log4("timeout", {
            ...protocol.toJSON()
          }, {
            file: "invitations-handler.ts",
            line: 255,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          stream.next({
            ...invitation,
            state: Invitation2.State.TIMEOUT
          });
        } else {
          log4.warn("auth failed", err, {
            file: "invitations-handler.ts",
            line: 258,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          stream.error(err);
        }
        void ctx.dispose();
      }
    });
    ctx.onDispose(() => {
      log4("complete", {
        ...protocol.toJSON()
      }, {
        file: "invitations-handler.ts",
        line: 266,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      stream.complete();
    });
    const createExtension = () => {
      let connectionCount = 0;
      const extension = new InvitationGuestExtension({
        onOpen: () => {
          scheduleTask(ctx, async () => {
            try {
              if (++connectionCount > 1) {
                throw new Error(`multiple connections detected: ${connectionCount}`);
              }
              scheduleTask(ctx, () => ctx.raise(new TimeoutError(timeout)), timeout);
              log4("connected", {
                ...protocol.toJSON()
              }, {
                file: "invitations-handler.ts",
                line: 284,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              stream.next({
                ...invitation,
                state: Invitation2.State.CONNECTED
              });
              log4("introduce", {
                ...protocol.toJSON()
              }, {
                file: "invitations-handler.ts",
                line: 288,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              const introductionResponse = await extension.rpc.InvitationHostService.introduce(protocol.createIntroduction());
              log4("introduce response", {
                ...protocol.toJSON(),
                response: introductionResponse
              }, {
                file: "invitations-handler.ts",
                line: 292,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              invitation.authMethod = introductionResponse.authMethod;
              if (introductionResponse.spaceKey) {
                invitation.spaceKey = introductionResponse.spaceKey;
              }
              if (isAuthenticationRequired(invitation)) {
                for (let attempt = 1; attempt <= MAX_OTP_ATTEMPTS; attempt++) {
                  log4("guest waiting for authentication code...", {}, {
                    file: "invitations-handler.ts",
                    line: 301,
                    scope: this,
                    callSite: (f, a) => f(...a)
                  });
                  stream.next({
                    ...invitation,
                    state: Invitation2.State.READY_FOR_AUTHENTICATION
                  });
                  const authCode = await authenticated.wait({
                    timeout
                  });
                  log4("sending authentication request", {}, {
                    file: "invitations-handler.ts",
                    line: 305,
                    scope: this,
                    callSite: (f, a) => f(...a)
                  });
                  stream.next({
                    ...invitation,
                    state: Invitation2.State.AUTHENTICATING
                  });
                  const response = await extension.rpc.InvitationHostService.authenticate({
                    authCode
                  });
                  if (response.status === void 0 || response.status === AuthenticationResponse.Status.OK) {
                    break;
                  }
                  if (response.status === AuthenticationResponse.Status.INVALID_OTP) {
                    if (attempt === MAX_OTP_ATTEMPTS) {
                      throw new Error(`Maximum retry attempts: ${MAX_OTP_ATTEMPTS}`);
                    } else {
                      log4("retrying invalid code", {
                        attempt
                      }, {
                        file: "invitations-handler.ts",
                        line: 316,
                        scope: this,
                        callSite: (f, a) => f(...a)
                      });
                      authenticated.reset();
                    }
                  }
                }
              } else {
                stream.next({
                  ...invitation,
                  state: Invitation2.State.READY_FOR_AUTHENTICATION
                });
              }
              log4("request admission", {
                ...protocol.toJSON()
              }, {
                file: "invitations-handler.ts",
                line: 327,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              const admissionRequest = await protocol.createAdmissionRequest();
              const admissionResponse = await extension.rpc.InvitationHostService.admit(admissionRequest);
              const result = await protocol.accept(admissionResponse, admissionRequest);
              log4("admitted by host", {
                ...protocol.toJSON()
              }, {
                file: "invitations-handler.ts",
                line: 335,
                scope: this,
                callSite: (f, a) => f(...a)
              });
              stream.next({
                ...invitation,
                ...result,
                state: Invitation2.State.SUCCESS
              });
            } catch (err) {
              if (err instanceof TimeoutError) {
                log4("timeout", {
                  ...protocol.toJSON()
                }, {
                  file: "invitations-handler.ts",
                  line: 339,
                  scope: this,
                  callSite: (f, a) => f(...a)
                });
                stream.next({
                  ...invitation,
                  state: Invitation2.State.TIMEOUT
                });
              } else {
                log4.warn("auth failed", err, {
                  file: "invitations-handler.ts",
                  line: 342,
                  scope: this,
                  callSite: (f, a) => f(...a)
                });
                stream.error(err);
              }
            } finally {
              await ctx.dispose();
            }
          });
        }
      });
      return extension;
    };
    scheduleTask(ctx, async () => {
      assert5(invitation.swarmKey);
      const topic = invitation.swarmKey;
      const swarmConnection = await this._networkManager.joinSwarm({
        topic,
        peerId: PublicKey4.random(),
        protocolProvider: createTeleportProtocolFactory(async (teleport) => {
          teleport.addExtension("dxos.halo.invitations", createExtension());
        }),
        topology: new StarTopology(topic)
      });
      ctx.onDispose(() => swarmConnection.close());
      stream.next({
        ...invitation,
        state: Invitation2.State.CONNECTING
      });
    });
    const observable = new AuthenticatingInvitationObservable({
      initialInvitation: invitation,
      subscriber: stream.observable,
      onCancel: async () => {
        stream.next({
          ...invitation,
          state: Invitation2.State.CANCELLED
        });
        await ctx.dispose();
      },
      onAuthenticate: async (code) => {
        authenticated.wake(code);
      }
    });
    return observable;
  }
};
var isAuthenticationRequired = (invitation) => invitation.authMethod !== Invitation2.AuthMethod.NONE;
var InvitationHostExtension = class extends RpcExtension {
  constructor(_callbacks) {
    super({
      exposed: {
        InvitationHostService: schema2.getService("dxos.halo.invitations.InvitationHostService")
      }
    });
    this._callbacks = _callbacks;
  }
  async getHandlers() {
    return {
      // TODO(dmaretskyi): For now this is just forwarding the data to callbacks since we don't have session-specific logic.
      // Perhaps in the future we will have more complex logic here.
      InvitationHostService: {
        introduce: async (request) => {
          return this._callbacks.introduce(request);
        },
        authenticate: async (request) => {
          return this._callbacks.authenticate(request);
        },
        admit: async (request) => {
          return this._callbacks.admit(request);
        }
      }
    };
  }
  async onOpen(context) {
    await super.onOpen(context);
    this._callbacks.onOpen();
  }
};
var InvitationGuestExtension = class extends RpcExtension {
  constructor(_callbacks) {
    super({
      requested: {
        InvitationHostService: schema2.getService("dxos.halo.invitations.InvitationHostService")
      }
    });
    this._callbacks = _callbacks;
  }
  async getHandlers() {
    return {};
  }
  async onOpen(context) {
    await super.onOpen(context);
    this._callbacks.onOpen();
  }
};

// packages/sdk/client-services/src/packlets/invitations/invitations-service.ts
import assert6 from "@dxos/node-std/assert";
import { Stream as Stream7 } from "@dxos/codec-protobuf";
import { log as log5 } from "@dxos/log";
import { Invitation as Invitation3 } from "@dxos/protocols/proto/dxos/client/services";
var InvitationsServiceImpl = class {
  constructor(_invitationsHandler, _getHandler) {
    this._invitationsHandler = _invitationsHandler;
    this._getHandler = _getHandler;
    this._createInvitations = /* @__PURE__ */ new Map();
    this._acceptInvitations = /* @__PURE__ */ new Map();
  }
  // TODO(burdon): Guest/host label.
  getLoggingContext() {
    return {};
  }
  createInvitation(invitation) {
    return new Stream7(({ next, close }) => {
      const handler = this._getHandler(invitation);
      log5("stream opened", this.getLoggingContext(), {
        file: "invitations-service.ts",
        line: 37,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      let invitationId;
      const observable = this._invitationsHandler.createInvitation(handler, invitation);
      observable.subscribe((invitation2) => {
        switch (invitation2.state) {
          case Invitation3.State.CONNECTING: {
            assert6(invitation2.invitationId);
            invitationId = invitation2.invitationId;
            this._createInvitations.set(invitation2.invitationId, observable);
            invitation2.state = Invitation3.State.CONNECTING;
            next(invitation2);
            break;
          }
          case Invitation3.State.CONNECTED: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.CONNECTED;
            next(invitation2);
            break;
          }
          case Invitation3.State.READY_FOR_AUTHENTICATION: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.READY_FOR_AUTHENTICATION;
            next(invitation2);
            break;
          }
          case Invitation3.State.AUTHENTICATING: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.AUTHENTICATING;
            next(invitation2);
            break;
          }
          case Invitation3.State.SUCCESS: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.SUCCESS;
            next(invitation2);
            break;
          }
          case Invitation3.State.CANCELLED: {
            assert6(invitationId);
            invitation2.invitationId = invitationId;
            invitation2.state = Invitation3.State.CANCELLED;
            next(invitation2);
            break;
          }
          case Invitation3.State.TIMEOUT: {
            assert6(invitationId);
            invitation2.invitationId = invitationId;
            invitation2.state = Invitation3.State.TIMEOUT;
            next(invitation2);
            break;
          }
        }
      }, (err) => {
        close(err);
      }, () => {
        close();
      });
      return (err) => {
        const context = this.getLoggingContext();
        if (err) {
          log5.warn("stream closed", {
            ...context,
            err
          }, {
            file: "invitations-service.ts",
            line: 103,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        } else {
          log5("stream closed", context, {
            file: "invitations-service.ts",
            line: 105,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
        this._createInvitations.delete(invitation.invitationId);
      };
    });
  }
  acceptInvitation(invitation) {
    return new Stream7(({ next, close }) => {
      log5("stream opened", this.getLoggingContext(), {
        file: "invitations-service.ts",
        line: 115,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      const handler = this._getHandler(invitation);
      let invitationId;
      const observable = this._invitationsHandler.acceptInvitation(handler, invitation);
      observable.subscribe((invitation2) => {
        switch (invitation2.state) {
          case Invitation3.State.CONNECTING: {
            assert6(invitation2.invitationId);
            invitationId = invitation2.invitationId;
            this._acceptInvitations.set(invitation2.invitationId, observable);
            invitation2.state = Invitation3.State.CONNECTING;
            next(invitation2);
            break;
          }
          case Invitation3.State.CONNECTED: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.CONNECTED;
            next(invitation2);
            break;
          }
          case Invitation3.State.READY_FOR_AUTHENTICATION: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.READY_FOR_AUTHENTICATION;
            next(invitation2);
            break;
          }
          case Invitation3.State.AUTHENTICATING: {
            assert6(invitation2.invitationId);
            invitation2.state = Invitation3.State.AUTHENTICATING;
            next(invitation2);
            break;
          }
          case Invitation3.State.SUCCESS: {
            invitation2.state = Invitation3.State.SUCCESS;
            next(invitation2);
            break;
          }
          case Invitation3.State.CANCELLED: {
            assert6(invitationId);
            invitation2.invitationId = invitationId;
            invitation2.state = Invitation3.State.CANCELLED;
            next(invitation2);
            break;
          }
          case Invitation3.State.TIMEOUT: {
            assert6(invitationId);
            invitation2.invitationId = invitationId;
            invitation2.state = Invitation3.State.TIMEOUT;
            next(invitation2);
            break;
          }
        }
      }, (err) => {
        close(err);
      }, () => {
        close();
      });
      return (err) => {
        const context = this.getLoggingContext();
        if (err) {
          log5.warn("stream closed", {
            ...context,
            err
          }, {
            file: "invitations-service.ts",
            line: 181,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        } else {
          log5("stream closed", context, {
            file: "invitations-service.ts",
            line: 183,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
        this._acceptInvitations.delete(invitation.invitationId);
      };
    });
  }
  async authenticate({ invitationId, authCode }) {
    log5("authenticating...", {}, {
      file: "invitations-service.ts",
      line: 192,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert6(invitationId);
    const observable = this._acceptInvitations.get(invitationId);
    if (!observable) {
      log5.warn("invalid invitation", {
        invitationId
      }, {
        file: "invitations-service.ts",
        line: 196,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } else {
      await observable.authenticate(authCode);
    }
  }
  async cancelInvitation({ invitationId }) {
    var _a;
    log5("cancelling...", {}, {
      file: "invitations-service.ts",
      line: 203,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert6(invitationId);
    const observable = (_a = this._createInvitations.get(invitationId)) != null ? _a : this._acceptInvitations.get(invitationId);
    if (!observable) {
      log5.warn("invalid invitation", {
        invitationId
      }, {
        file: "invitations-service.ts",
        line: 207,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } else {
      await (observable == null ? void 0 : observable.cancel());
    }
  }
};

// packages/sdk/client-services/src/packlets/invitations/space-invitation-protocol.ts
import assert7 from "@dxos/node-std/assert";
import { createAdmissionCredentials, getCredentialAssertion } from "@dxos/credentials";
import { writeMessages as writeMessages2 } from "@dxos/feed-store";
import { log as log6 } from "@dxos/log";
import { Invitation as Invitation4 } from "@dxos/protocols/proto/dxos/client/services";
var SpaceInvitationProtocol = class {
  constructor(_spaceManager, _signingContext, _keyring, _spaceKey) {
    this._spaceManager = _spaceManager;
    this._signingContext = _signingContext;
    this._keyring = _keyring;
    this._spaceKey = _spaceKey;
  }
  toJSON() {
    return {
      deviceKey: this._signingContext.deviceKey,
      spaceKey: this._spaceKey
    };
  }
  getInvitationContext() {
    return {
      kind: Invitation4.Kind.SPACE,
      spaceKey: this._spaceKey
    };
  }
  async admit(request, guestProfile) {
    var _a;
    assert7(this._spaceKey);
    const space = await this._spaceManager.spaces.get(this._spaceKey);
    assert7(space);
    assert7(request.space);
    const { identityKey, deviceKey } = request.space;
    log6("writing guest credentials", {
      host: this._signingContext.deviceKey,
      guest: deviceKey
    }, {
      file: "space-invitation-protocol.ts",
      line: 51,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const credentials = await createAdmissionCredentials(this._signingContext.credentialSigner, identityKey, space.key, space.inner.genesisFeedKey, guestProfile);
    assert7(credentials[0].credential);
    const spaceMemberCredential = credentials[0].credential.credential;
    assert7(getCredentialAssertion(spaceMemberCredential)["@type"] === "dxos.halo.credentials.SpaceMember");
    await writeMessages2(space.inner.controlPipeline.writer, credentials);
    return {
      space: {
        credential: spaceMemberCredential,
        controlTimeframe: space.inner.controlPipeline.state.timeframe,
        dataTimeframe: (_a = space.dataPipeline.pipelineState) == null ? void 0 : _a.timeframe
      }
    };
  }
  createIntroduction() {
    return {
      profile: this._signingContext.profile
    };
  }
  async createAdmissionRequest() {
    const controlFeedKey = await this._keyring.createKey();
    const dataFeedKey = await this._keyring.createKey();
    return {
      space: {
        identityKey: this._signingContext.identityKey,
        deviceKey: this._signingContext.deviceKey,
        controlFeedKey,
        dataFeedKey
      }
    };
  }
  async accept(response) {
    assert7(response.space);
    const { credential, controlTimeframe, dataTimeframe } = response.space;
    const assertion = getCredentialAssertion(credential);
    assert7(assertion["@type"] === "dxos.halo.credentials.SpaceMember", "Invalid credential");
    assert7(credential.subject.id.equals(this._signingContext.identityKey));
    await this._spaceManager.acceptSpace({
      spaceKey: assertion.spaceKey,
      genesisFeedKey: assertion.genesisFeedKey,
      controlTimeframe,
      dataTimeframe
    });
    await this._signingContext.recordCredential(credential);
    return {
      spaceKey: assertion.spaceKey
    };
  }
};

// packages/sdk/client-services/src/packlets/storage/storage.ts
import { InvalidConfigError } from "@dxos/errors";
import { Runtime } from "@dxos/protocols/proto/dxos/config";
import { createStorage, StorageType } from "@dxos/random-access-storage";
var StorageDriver = Runtime.Client.Storage.StorageDriver;
var createStorageObjects = (config) => {
  const { path = "dxos/storage", storageType, keyStorage, persistent = false } = config != null ? config : {};
  if (persistent && storageType === StorageDriver.RAM) {
    throw new InvalidConfigError("RAM storage cannot be used in persistent mode.");
  }
  if (!persistent && storageType !== void 0 && storageType !== StorageDriver.RAM) {
    throw new InvalidConfigError("Cannot use a persistent storage in not persistent mode.");
  }
  if (persistent && keyStorage === StorageDriver.RAM) {
    throw new InvalidConfigError("RAM key storage cannot be used in persistent mode.");
  }
  if (!persistent && keyStorage !== StorageDriver.RAM && keyStorage !== void 0) {
    throw new InvalidConfigError("Cannot use a persistent key storage in not persistent mode.");
  }
  return {
    storage: createStorage({
      type: persistent ? toStorageType(storageType) : StorageType.RAM,
      root: `${path}/`
    })
  };
};
var toStorageType = (type) => {
  switch (type) {
    case void 0:
      return void 0;
    case StorageDriver.RAM:
      return StorageType.RAM;
    case StorageDriver.CHROME:
      return StorageType.CHROME;
    case StorageDriver.FIREFOX:
      return StorageType.FIREFOX;
    case StorageDriver.IDB:
      return StorageType.IDB;
    case StorageDriver.NODE:
      return StorageType.NODE;
    case StorageDriver.WEBFS:
      return StorageType.WEBFS;
    default:
      throw new Error(`Invalid storage type: ${StorageDriver[type]}`);
  }
};

// packages/sdk/client-services/src/packlets/vault/shell-runtime.ts
import assert8 from "@dxos/node-std/assert";
import { Event as Event3 } from "@dxos/async";
import { appServiceBundle, shellServiceBundle } from "@dxos/client";
import { ShellLayout } from "@dxos/protocols/proto/dxos/iframe";
import { createProtoRpcPeer } from "@dxos/rpc";
var ShellRuntimeImpl = class {
  constructor(_port) {
    this._port = _port;
    this.layoutUpdate = new Event3();
    this._layout = ShellLayout.DEFAULT;
  }
  get layout() {
    return this._layout;
  }
  get invitationCode() {
    return this._invitationCode;
  }
  get spaceKey() {
    return this._spaceKey;
  }
  setLayout(layout, options = {}) {
    this._layout = layout;
    this._invitationCode = options.invitationCode;
    this._spaceKey = options.spaceKey;
    this.layoutUpdate.emit({
      layout,
      ...options
    });
  }
  async setAppContext(context) {
    assert8(this._appRpc, "runtime not open");
    await this._appRpc.rpc.AppService.setContext(context);
  }
  async open() {
    this._appRpc = createProtoRpcPeer({
      requested: appServiceBundle,
      exposed: shellServiceBundle,
      handlers: {
        ShellService: {
          setLayout: async (request) => {
            this._layout = request.layout;
            this._invitationCode = request.invitationCode;
            this._spaceKey = request.spaceKey;
            this.layoutUpdate.emit(request);
          }
        }
      },
      port: this._port
    });
    await this._appRpc.open();
  }
  async close() {
    var _a;
    await ((_a = this._appRpc) == null ? void 0 : _a.close());
    this._appRpc = void 0;
  }
};
var MemoryShellRuntime = class {
  constructor({ layout, invitationCode, spaceKey } = {}) {
    this.layoutUpdate = new Event3();
    this.contextUpdate = new Event3();
    this._layout = layout != null ? layout : ShellLayout.DEFAULT;
    this._invitationCode = invitationCode;
    this._spaceKey = spaceKey;
  }
  get layout() {
    return this._layout;
  }
  get invitationCode() {
    return this._invitationCode;
  }
  get spaceKey() {
    return this._spaceKey;
  }
  setLayout(layout, options = {}) {
    this._layout = layout;
    this._invitationCode = options.invitationCode;
    this._spaceKey = options.spaceKey;
    this.layoutUpdate.emit({
      layout,
      ...options
    });
  }
  async setAppContext(context) {
    this.contextUpdate.emit(context);
  }
};

// packages/sdk/client-services/src/packlets/vault/iframe-host-runtime.ts
import { Trigger as Trigger8 } from "@dxos/async";
import { log as log17, logInfo as logInfo3 } from "@dxos/log";
import { MemorySignalManager as MemorySignalManager3, MemorySignalManagerContext as MemorySignalManagerContext3, WebsocketSignalManager as WebsocketSignalManager3 } from "@dxos/messaging";
import { createWebRTCTransportFactory as createWebRTCTransportFactory2, NetworkManager as NetworkManager3 } from "@dxos/network-manager";
import { getAsyncValue } from "@dxos/util";

// packages/sdk/client-services/src/packlets/services/service-host.ts
import assert13 from "@dxos/node-std/assert";
import { Event as Event7 } from "@dxos/async";
import { clientServiceBundle, createDefaultModelFactory, PublicKey as PublicKey9 } from "@dxos/client";
import { DataServiceImpl } from "@dxos/echo-pipeline";
import { log as log15 } from "@dxos/log";
import { trace as trace4 } from "@dxos/protocols";
import { SystemStatus } from "@dxos/protocols/proto/dxos/client/services";

// packages/sdk/client-services/src/packlets/deprecated/tracing.ts
var TracingServiceImpl = class {
  constructor(_config) {
    this._config = _config;
  }
  async setTracingOptions() {
    throw new Error("Tracing not available.");
  }
  subscribeToRpcTrace() {
    throw new Error("Tracing not available.");
  }
};

// packages/sdk/client-services/src/packlets/devices/devices-service.ts
import { EventSubscriptions as EventSubscriptions2 } from "@dxos/async";
import { Stream as Stream8 } from "@dxos/codec-protobuf";
import { DeviceKind } from "@dxos/protocols/proto/dxos/client/services";
var DevicesServiceImpl = class {
  constructor(_identityManager) {
    this._identityManager = _identityManager;
  }
  updateDevice(request) {
    throw new Error("Method not implemented.");
  }
  queryDevices() {
    return new Stream8(({ next }) => {
      const update = () => {
        var _a;
        const deviceKeys = (_a = this._identityManager.identity) == null ? void 0 : _a.authorizedDeviceKeys;
        if (!deviceKeys) {
          next({
            devices: []
          });
        } else {
          next({
            devices: Array.from(deviceKeys.values()).map((key) => {
              var _a2;
              return {
                deviceKey: key,
                kind: ((_a2 = this._identityManager.identity) == null ? void 0 : _a2.deviceKey.equals(key)) ? DeviceKind.CURRENT : DeviceKind.TRUSTED
              };
            })
          });
        }
      };
      const subscriptions = new EventSubscriptions2();
      subscriptions.add(this._identityManager.stateUpdate.on(() => {
        update();
        if (this._identityManager.identity) {
          subscriptions.add(this._identityManager.identity.stateUpdate.on(() => {
            update();
          }));
        }
      }));
      update();
      return () => subscriptions.clear();
    });
  }
};

// packages/sdk/client-services/src/packlets/network/network-service.ts
import { Stream as Stream9 } from "@dxos/codec-protobuf";
var NetworkServiceImpl = class {
  constructor(networkManager) {
    this.networkManager = networkManager;
  }
  subscribeToNetworkStatus() {
    return new Stream9(({ next }) => {
      const unsubscribe = this.networkManager.connectionStateChanged.on((state) => next({
        state
      }));
      next({
        state: this.networkManager.connectionState
      });
      return unsubscribe;
    });
  }
  async setNetworkOptions(request) {
    await this.networkManager.setConnectionState(request.state);
  }
};

// packages/sdk/client-services/src/packlets/spaces/data-space-manager.ts
import assert10 from "@dxos/node-std/assert";
import { Event as Event6, synchronized, trackLeaks as trackLeaks2 } from "@dxos/async";
import { SpaceState as SpaceState2 } from "@dxos/client";
import { cancelWithContext, Context as Context6 } from "@dxos/context";
import { getCredentialAssertion as getCredentialAssertion2 } from "@dxos/credentials";
import { SnapshotManager, spaceGenesis } from "@dxos/echo-pipeline";
import { PublicKey as PublicKey7 } from "@dxos/keys";
import { log as log9 } from "@dxos/log";
import { trace as trace2 } from "@dxos/protocols";
import { Gossip, Presence } from "@dxos/teleport-extension-gossip";
import { ComplexMap as ComplexMap3, deferFunction as deferFunction2 } from "@dxos/util";

// packages/sdk/client-services/src/packlets/spaces/data-space.ts
import { Event as Event5, scheduleTask as scheduleTask3, trackLeaks } from "@dxos/async";
import { SpaceState } from "@dxos/client";
import { Context as Context5 } from "@dxos/context";
import { timed } from "@dxos/debug";
import { createMappedFeedWriter, DataPipeline } from "@dxos/echo-pipeline";
import { CancelledError, SystemError } from "@dxos/errors";
import { PublicKey as PublicKey6 } from "@dxos/keys";
import { log as log8 } from "@dxos/log";
import { AdmittedFeed as AdmittedFeed3 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { ComplexSet as ComplexSet2 } from "@dxos/util";

// packages/sdk/client-services/src/packlets/spaces/notarization-plugin.ts
import assert9 from "@dxos/node-std/assert";
import { DeferredTask, Event as Event4, scheduleTask as scheduleTask2, sleep as sleep2, TimeoutError as TimeoutError2, Trigger as Trigger3 } from "@dxos/async";
import { Context as Context4, rejectOnDispose } from "@dxos/context";
import { PublicKey as PublicKey5 } from "@dxos/keys";
import { log as log7 } from "@dxos/log";
import { schema as schema3 } from "@dxos/protocols";
import { RpcExtension as RpcExtension2 } from "@dxos/teleport";
import { ComplexMap as ComplexMap2, ComplexSet, entry } from "@dxos/util";
var DEFAULT_RETRY_TIMEOUT = 1e3;
var DEFAULT_SUCCESS_DELAY = 1e3;
var DEFAULT_NOTARIZE_TIMEOUT = 1e4;
var NotarizationPlugin = class {
  constructor() {
    this._ctx = new Context4();
    this._extensionOpened = new Event4();
    this._extensions = /* @__PURE__ */ new Set();
    this._processedCredentials = new ComplexSet(PublicKey5.hash);
    this._processCredentialsTriggers = new ComplexMap2(PublicKey5.hash);
  }
  async open() {
  }
  async close() {
    await this._ctx.dispose();
  }
  /**
  * Request credentials to be notarized.
  */
  async notarize({ ctx: opCtx, credentials, timeout = DEFAULT_NOTARIZE_TIMEOUT, retryTimeout = DEFAULT_RETRY_TIMEOUT, successDelay = DEFAULT_SUCCESS_DELAY }) {
    log7("notarize", {
      credentials
    }, {
      file: "notarization-plugin.ts",
      line: 85,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert9(credentials.every((credential) => credential.id), "Credentials must have an id");
    const errors = new Trigger3();
    const ctx = this._ctx.derive({
      onError: (err) => {
        log7.warn("Notarization error", {
          err
        }, {
          file: "notarization-plugin.ts",
          line: 94,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        void ctx.dispose();
        errors.throw(err);
      }
    });
    opCtx == null ? void 0 : opCtx.onDispose(() => ctx.dispose());
    if (timeout !== 0) {
      scheduleTask2(ctx, () => {
        log7.warn("Notarization timeout", {
          timeout,
          peers: Array.from(this._extensions).map((extension) => extension.remotePeerId)
        }, {
          file: "notarization-plugin.ts",
          line: 106,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        void ctx.dispose();
        errors.throw(new TimeoutError2(timeout, "Notarization timed out"));
      }, timeout);
    }
    const allNotarized = Promise.all(credentials.map((credential) => this._waitUntilProcessed(credential.id)));
    const peersTried = /* @__PURE__ */ new Set();
    const notarizeTask = new DeferredTask(ctx, async () => {
      try {
        if (this._extensions.size === 0) {
          return;
        }
        const peer = [
          ...this._extensions
        ].find((peer2) => !peersTried.has(peer2));
        if (!peer) {
          log7.warn("Exhausted all peers to notarize with", {
            retryIn: retryTimeout
          }, {
            file: "notarization-plugin.ts",
            line: 131,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          peersTried.clear();
          scheduleTask2(ctx, () => notarizeTask.schedule(), retryTimeout);
          return;
        }
        peersTried.add(peer);
        log7("try notarizing", {
          peer: peer.localPeerId,
          credentialId: credentials.map((credential) => credential.id)
        }, {
          file: "notarization-plugin.ts",
          line: 138,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        await peer.rpc.NotarizationService.notarize({
          credentials: credentials.filter((credential) => !this._processedCredentials.has(credential.id))
        });
        log7("success", {}, {
          file: "notarization-plugin.ts",
          line: 142,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        await sleep2(successDelay);
      } catch (err) {
        log7.warn("error notarizing (recoverable)", err, {
          file: "notarization-plugin.ts",
          line: 145,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        notarizeTask.schedule();
      }
    });
    notarizeTask.schedule();
    this._extensionOpened.on(ctx, () => notarizeTask.schedule());
    try {
      await Promise.race([
        rejectOnDispose(ctx),
        allNotarized,
        errors.wait()
      ]);
      log7("done", {}, {
        file: "notarization-plugin.ts",
        line: 155,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } finally {
      await ctx.dispose();
    }
  }
  /**
  * Called with credentials arriving from the control pipeline.
  */
  async process(credential) {
    var _a;
    if (!credential.id) {
      return;
    }
    (_a = this._processCredentialsTriggers.get(credential.id)) == null ? void 0 : _a.wake();
    this._processedCredentials.add(credential.id);
    this._processCredentialsTriggers.delete(credential.id);
  }
  setWriter(writer) {
    assert9(!this._writer, "Writer already set.");
    this._writer = writer;
  }
  async _waitUntilProcessed(id) {
    if (this._processedCredentials.has(id)) {
      return;
    }
    await entry(this._processCredentialsTriggers, id).orInsert(new Trigger3()).value.wait();
  }
  /**
  * Requests from other peers to notarize credentials.
  */
  async _onNotarize(request) {
    var _a;
    if (!this._writer) {
      throw new Error("Writer not set");
    }
    for (const credential of (_a = request.credentials) != null ? _a : []) {
      assert9(credential.id, "Credential must have an id");
      if (this._processedCredentials.has(credential.id)) {
        continue;
      }
      await this._writer.write(credential);
    }
  }
  createExtension() {
    const extension = new NotarizationTeleportExtension({
      onOpen: async () => {
        log7("extension opened", {
          peer: extension.localPeerId
        }, {
          file: "notarization-plugin.ts",
          line: 204,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._extensions.add(extension);
        this._extensionOpened.emit();
      },
      onClose: async () => {
        log7("extension closed", {
          peer: extension.localPeerId
        }, {
          file: "notarization-plugin.ts",
          line: 209,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._extensions.delete(extension);
      },
      onNotarize: this._onNotarize.bind(this)
    });
    return extension;
  }
};
var NotarizationTeleportExtension = class extends RpcExtension2 {
  constructor(_params) {
    super({
      requested: {
        NotarizationService: schema3.getService("dxos.mesh.teleport.notarization.NotarizationService")
      },
      exposed: {
        NotarizationService: schema3.getService("dxos.mesh.teleport.notarization.NotarizationService")
      }
    });
    this._params = _params;
  }
  async getHandlers() {
    return {
      NotarizationService: {
        notarize: async (request) => {
          await this._params.onNotarize(request);
        }
      }
    };
  }
  async onOpen(ctx) {
    await super.onOpen(ctx);
    await this._params.onOpen();
  }
  async onClose(err) {
    await this._params.onClose();
    await super.onClose(err);
  }
};

// packages/sdk/client-services/src/packlets/spaces/data-space.ts
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AUTH_TIMEOUT2 = 3e4;
var DataSpace = class DataSpace2 {
  constructor(params) {
    this._ctx = new Context5();
    this._state = SpaceState.CLOSED;
    this.stateUpdate = new Event5();
    var _a;
    this._inner = params.inner;
    this._inner.stateUpdate.on(this._ctx, () => this.stateUpdate.emit());
    this._gossip = params.gossip;
    this._presence = params.presence;
    this._keyring = params.keyring;
    this._feedStore = params.feedStore;
    this._metadataStore = params.metadataStore;
    this._signingContext = params.signingContext;
    this._callbacks = (_a = params.callbacks) != null ? _a : {};
    this._dataPipeline = new DataPipeline({
      modelFactory: params.modelFactory,
      metadataStore: params.metadataStore,
      snapshotManager: params.snapshotManager,
      memberKey: params.memberKey,
      spaceKey: this._inner.key,
      feedInfoProvider: (feedKey) => this._inner.spaceState.feeds.get(feedKey),
      snapshotId: params.snapshotId
    });
    this.authVerifier = new TrustedKeySetAuthVerifier({
      trustedKeysProvider: () => new ComplexSet2(PublicKey6.hash, Array.from(this._inner.spaceState.members.keys())),
      update: this._inner.stateUpdate,
      authTimeout: AUTH_TIMEOUT2
    });
    this._notarizationPluginConsumer = this._inner.spaceState.registerProcessor(new NotarizationPlugin());
  }
  get key() {
    return this._inner.key;
  }
  get isOpen() {
    return this._inner.isOpen;
  }
  get state() {
    return this._state;
  }
  // TODO(burdon): Can we mark this for debugging only?
  get inner() {
    return this._inner;
  }
  get dataPipeline() {
    return this._dataPipeline;
  }
  get presence() {
    return this._presence;
  }
  get notarizationPlugin() {
    return this._notarizationPluginConsumer.processor;
  }
  async open() {
    await this.notarizationPlugin.open();
    await this._notarizationPluginConsumer.open();
    await this._inner.open();
    this._state = SpaceState.INACTIVE;
  }
  async close() {
    this._state = SpaceState.CLOSED;
    await this._ctx.dispose();
    await this._dataPipeline.close();
    await this.authVerifier.close();
    await this._inner.close();
    await this._notarizationPluginConsumer.close();
    await this.notarizationPlugin.close();
    await this._presence.destroy();
  }
  async postMessage(channel, message) {
    return this._gossip.postMessage(channel, message);
  }
  listen(channel, callback) {
    return this._gossip.listen(channel, callback);
  }
  /**
  * Initialize the data pipeline in a separate task.
  */
  initializeDataPipelineAsync() {
    scheduleTask3(this._ctx, async () => {
      try {
        await this.initializeDataPipeline();
      } catch (err) {
        if (err instanceof CancelledError) {
          log8("Data pipeline initialization cancelled", err, {
            file: "data-space.ts",
            line: 178,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return;
        }
        log8.error("Error initializing data pipeline", err, {
          file: "data-space.ts",
          line: 182,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
    });
  }
  async initializeDataPipeline() {
    var _a, _b, _c, _d;
    if (this._state !== SpaceState.INACTIVE) {
      throw new SystemError("Invalid operation");
    }
    this._state = SpaceState.INITIALIZING;
    await this._inner.controlPipeline.state.waitUntilReachedTargetTimeframe({
      ctx: this._ctx,
      breakOnStall: false
    });
    await this._createWritableFeeds();
    log8("Writable feeds created", {}, {
      file: "data-space.ts",
      line: 200,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this.stateUpdate.emit();
    this.notarizationPlugin.setWriter(createMappedFeedWriter((credential) => ({
      credential: {
        credential
      }
    }), this._inner.controlPipeline.writer));
    await this._dataPipeline.open({
      openPipeline: async (start) => {
        const pipeline = await this._inner.createDataPipeline({
          start
        });
        await pipeline.start();
        return pipeline;
      }
    });
    log8("waiting for data pipeline to reach target timeframe", {}, {
      file: "data-space.ts",
      line: 220,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._dataPipeline.pipelineState.waitUntilReachedTargetTimeframe({
      ctx: this._ctx,
      breakOnStall: false
    });
    log8("data pipeline ready", {}, {
      file: "data-space.ts",
      line: 227,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await ((_b = (_a = this._callbacks).beforeReady) == null ? void 0 : _b.call(_a));
    this._state = SpaceState.READY;
    this.stateUpdate.emit();
    await ((_d = (_c = this._callbacks).afterReady) == null ? void 0 : _d.call(_c));
  }
  async _createWritableFeeds() {
    const credentials = [];
    if (!this.inner.controlFeedKey) {
      const controlFeed = await this._feedStore.openFeed(await this._keyring.createKey(), {
        writable: true
      });
      this.inner.setControlFeed(controlFeed);
      credentials.push(await this._signingContext.credentialSigner.createCredential({
        subject: controlFeed.key,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          spaceKey: this.key,
          deviceKey: this._signingContext.deviceKey,
          identityKey: this._signingContext.identityKey,
          designation: AdmittedFeed3.Designation.CONTROL
        }
      }));
    }
    if (!this.inner.dataFeedKey) {
      const dataFeed = await this._feedStore.openFeed(await this._keyring.createKey(), {
        writable: true
      });
      this.inner.setDataFeed(dataFeed);
      credentials.push(await this._signingContext.credentialSigner.createCredential({
        subject: dataFeed.key,
        assertion: {
          "@type": "dxos.halo.credentials.AdmittedFeed",
          spaceKey: this.key,
          deviceKey: this._signingContext.deviceKey,
          identityKey: this._signingContext.identityKey,
          designation: AdmittedFeed3.Designation.DATA
        }
      }));
    }
    if (credentials.length > 0) {
      await this.notarizationPlugin.notarize({
        ctx: this._ctx,
        credentials,
        timeout: 0
      });
    }
    await this._metadataStore.setWritableFeedKeys(this.key, this.inner.controlFeedKey, this.inner.dataFeedKey);
  }
};
__decorate([
  timed(1e4)
], DataSpace.prototype, "_createWritableFeeds", null);
DataSpace = __decorate([
  trackLeaks("open", "close")
], DataSpace);

// packages/sdk/client-services/src/packlets/spaces/data-space-manager.ts
var __decorate2 = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var DataSpaceManager = class DataSpaceManager2 {
  constructor(_spaceManager, _metadataStore, _dataServiceSubscriptions, _keyring, _signingContext, _modelFactory, _feedStore, _snapshotStore) {
    this._spaceManager = _spaceManager;
    this._metadataStore = _metadataStore;
    this._dataServiceSubscriptions = _dataServiceSubscriptions;
    this._keyring = _keyring;
    this._signingContext = _signingContext;
    this._modelFactory = _modelFactory;
    this._feedStore = _feedStore;
    this._snapshotStore = _snapshotStore;
    this._ctx = new Context6();
    this.updated = new Event6();
    this._spaces = new ComplexMap3(PublicKey7.hash);
    this._isOpen = false;
    this._instanceId = PublicKey7.random().toHex();
  }
  // TODO(burdon): Remove.
  get spaces() {
    return this._spaces;
  }
  async open() {
    log9("open", {}, {
      file: "data-space-manager.ts",
      line: 83,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    log9.trace("dxos.echo.data-space-manager", trace2.begin({
      id: this._instanceId,
      parentId: this._traceParent
    }), {
      file: "data-space-manager.ts",
      line: 84,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._metadataStore.load();
    log9("metadata loaded", {
      spaces: this._metadataStore.spaces.length
    }, {
      file: "data-space-manager.ts",
      line: 86,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    for (const spaceMetadata of this._metadataStore.spaces) {
      log9("load space", {
        spaceMetadata
      }, {
        file: "data-space-manager.ts",
        line: 89,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      const space = await this._constructSpace(spaceMetadata);
      space.initializeDataPipelineAsync();
    }
    this._isOpen = true;
    this.updated.emit();
  }
  async close() {
    log9("close", {}, {
      file: "data-space-manager.ts",
      line: 100,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._isOpen = false;
    await this._ctx.dispose();
    for (const space of this._spaces.values()) {
      await space.close();
    }
    log9.trace("dxos.echo.data-space-manager", trace2.end({
      id: this._instanceId
    }), {
      file: "data-space-manager.ts",
      line: 106,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  /**
  * Creates a new space writing the genesis credentials to the control feed.
  */
  async createSpace() {
    assert10(this._isOpen, "Not open.");
    const spaceKey = await this._keyring.createKey();
    const controlFeedKey = await this._keyring.createKey();
    const dataFeedKey = await this._keyring.createKey();
    const metadata = {
      key: spaceKey,
      genesisFeedKey: controlFeedKey,
      controlFeedKey,
      dataFeedKey
    };
    log9("creating space...", {
      spaceKey
    }, {
      file: "data-space-manager.ts",
      line: 125,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const space = await this._constructSpace(metadata);
    const credentials = await spaceGenesis(this._keyring, this._signingContext, space.inner);
    await this._metadataStore.addSpace(metadata);
    const memberCredential = credentials[1];
    assert10(getCredentialAssertion2(memberCredential)["@type"] === "dxos.halo.credentials.SpaceMember");
    await this._signingContext.recordCredential(memberCredential);
    await space.initializeDataPipeline();
    this.updated.emit();
    return space;
  }
  // TODO(burdon): Rename join space.
  async acceptSpace(opts) {
    log9("accept space", {
      opts
    }, {
      file: "data-space-manager.ts",
      line: 144,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert10(this._isOpen, "Not open.");
    assert10(!this._spaces.has(opts.spaceKey), "Space already exists.");
    const metadata = {
      key: opts.spaceKey,
      genesisFeedKey: opts.genesisFeedKey,
      controlTimeframe: opts.controlTimeframe,
      dataTimeframe: opts.dataTimeframe
    };
    const space = await this._constructSpace(metadata);
    await this._metadataStore.addSpace(metadata);
    space.initializeDataPipelineAsync();
    this.updated.emit();
    return space;
  }
  /**
  * Wait until the space data pipeline is fully initialized.
  * Used by invitation handler.
  * TODO(dmaretskyi): Consider removing.
  */
  async waitUntilSpaceReady(spaceKey) {
    await cancelWithContext(this._ctx, this.updated.waitForCondition(() => {
      const space = this._spaces.get(spaceKey);
      return !!space && space.state === SpaceState2.READY;
    }));
  }
  async _constructSpace(metadata) {
    log9("construct space", {
      metadata
    }, {
      file: "data-space-manager.ts",
      line: 180,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const gossip = new Gossip({
      localPeerId: this._signingContext.deviceKey
    });
    const presence = new Presence({
      announceInterval: 500,
      offlineTimeout: 5e3,
      identityKey: this._signingContext.identityKey,
      gossip
    });
    const snapshotManager = new SnapshotManager(this._snapshotStore);
    const controlFeed = metadata.controlFeedKey && await this._feedStore.openFeed(metadata.controlFeedKey, {
      writable: true
    });
    const dataFeed = metadata.dataFeedKey && await this._feedStore.openFeed(metadata.dataFeedKey, {
      writable: true
    });
    const space = await this._spaceManager.constructSpace({
      metadata,
      swarmIdentity: {
        peerKey: this._signingContext.deviceKey,
        credentialProvider: createAuthProvider(this._signingContext.credentialSigner),
        credentialAuthenticator: deferFunction2(() => dataSpace.authVerifier.verifier)
      },
      onNetworkConnection: (session) => {
        session.addExtension("dxos.mesh.teleport.gossip", gossip.createExtension({
          remotePeerId: session.remotePeerId
        }));
        session.addExtension("dxos.mesh.teleport.objectsync", snapshotManager.objectSync.createExtension());
        session.addExtension("dxos.mesh.teleport.notarization", dataSpace.notarizationPlugin.createExtension());
      }
    });
    controlFeed && space.setControlFeed(controlFeed);
    dataFeed && space.setDataFeed(dataFeed);
    const dataSpace = new DataSpace({
      inner: space,
      modelFactory: this._modelFactory,
      metadataStore: this._metadataStore,
      snapshotManager,
      gossip,
      presence,
      memberKey: this._signingContext.identityKey,
      keyring: this._keyring,
      feedStore: this._feedStore,
      signingContext: this._signingContext,
      snapshotId: metadata.snapshot,
      callbacks: {
        beforeReady: async () => {
          log9("before space ready", {
            space: space.key
          }, {
            file: "data-space-manager.ts",
            line: 229,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          this._dataServiceSubscriptions.registerSpace(space.key, dataSpace.dataPipeline.databaseBackend.createDataServiceHost());
        },
        afterReady: async () => {
          log9("after space ready", {
            space: space.key,
            open: this._isOpen
          }, {
            file: "data-space-manager.ts",
            line: 236,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          if (this._isOpen) {
            this.updated.emit();
          }
        }
      }
    });
    await dataSpace.open();
    if (metadata.controlTimeframe) {
      dataSpace.inner.controlPipeline.state.setTargetTimeframe(metadata.controlTimeframe);
    }
    if (metadata.dataTimeframe) {
      dataSpace.dataPipeline.setTargetTimeframe(metadata.dataTimeframe);
    }
    this._spaces.set(metadata.key, dataSpace);
    return dataSpace;
  }
};
__decorate2([
  synchronized
], DataSpaceManager.prototype, "open", null);
__decorate2([
  synchronized
], DataSpaceManager.prototype, "close", null);
__decorate2([
  synchronized
], DataSpaceManager.prototype, "createSpace", null);
__decorate2([
  synchronized
], DataSpaceManager.prototype, "acceptSpace", null);
DataSpaceManager = __decorate2([
  trackLeaks2("open", "close")
], DataSpaceManager);

// packages/sdk/client-services/src/packlets/spaces/spaces-service.ts
import { EventSubscriptions as EventSubscriptions3, scheduleTask as scheduleTask4 } from "@dxos/async";
import { Stream as Stream10 } from "@dxos/codec-protobuf";
import { raise, todo as todo2 } from "@dxos/debug";
import { SpaceNotFoundError } from "@dxos/echo-pipeline";
import { log as log10 } from "@dxos/log";
import { SpaceMember } from "@dxos/protocols/proto/dxos/client/services";
import { humanize } from "@dxos/util";
var TIMEFRAME_UPDATE_DEBOUNCE_TIME = 500;
var SpacesServiceImpl = class {
  constructor(_identityManager, _spaceManager, _dataServiceSubscriptions, _getDataSpaceManager) {
    this._identityManager = _identityManager;
    this._spaceManager = _spaceManager;
    this._dataServiceSubscriptions = _dataServiceSubscriptions;
    this._getDataSpaceManager = _getDataSpaceManager;
  }
  async createSpace() {
    if (!this._identityManager.identity) {
      throw new Error("This device has no HALO identity available. See https://docs.dxos.org/guide/halo");
    }
    const dataSpaceManager = await this._getDataSpaceManager();
    const space = await dataSpaceManager.createSpace();
    return this._transformSpace(space);
  }
  async updateSpace(request) {
    todo2();
  }
  querySpaces() {
    return new Stream10(({ next, ctx }) => {
      const onUpdate = async () => {
        const dataSpaceManager = await this._getDataSpaceManager();
        const spaces = Array.from(dataSpaceManager.spaces.values()).map((space) => this._transformSpace(space));
        log10("update", {
          spaces
        }, {
          file: "spaces-service.ts",
          line: 60,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        next({
          spaces
        });
      };
      scheduleTask4(ctx, async () => {
        const dataSpaceManager = await this._getDataSpaceManager();
        const subscriptions = new EventSubscriptions3();
        ctx.onDispose(() => subscriptions.clear());
        const subscribeSpaces = () => {
          subscriptions.clear();
          for (const space of dataSpaceManager.spaces.values()) {
            subscriptions.add(space.stateUpdate.on(ctx, onUpdate));
            subscriptions.add(space.presence.updated.on(ctx, onUpdate));
            space.inner.controlPipeline.state.timeframeUpdate.debounce(TIMEFRAME_UPDATE_DEBOUNCE_TIME).on(ctx, onUpdate);
            if (space.dataPipeline.pipelineState) {
              subscriptions.add(space.dataPipeline.pipelineState.timeframeUpdate.debounce(TIMEFRAME_UPDATE_DEBOUNCE_TIME).on(ctx, onUpdate));
            }
          }
        };
        dataSpaceManager.updated.on(ctx, () => {
          subscribeSpaces();
          void onUpdate();
        });
        subscribeSpaces();
        void onUpdate();
      });
      if (!this._identityManager.identity) {
        next({
          spaces: []
        });
      }
    });
  }
  async postMessage({ spaceKey, channel, message }) {
    var _a;
    const dataSpaceManager = await this._getDataSpaceManager();
    const space = (_a = dataSpaceManager.spaces.get(spaceKey)) != null ? _a : raise(new SpaceNotFoundError(spaceKey));
    await space.postMessage(getChannelId(channel), message);
  }
  subscribeMessages({ spaceKey, channel }) {
    return new Stream10(({ ctx, next }) => {
      scheduleTask4(ctx, async () => {
        var _a;
        const dataSpaceManager = await this._getDataSpaceManager();
        const space = (_a = dataSpaceManager.spaces.get(spaceKey)) != null ? _a : raise(new SpaceNotFoundError(spaceKey));
        const handle = space.listen(getChannelId(channel), (message) => {
          next(message);
        });
        ctx.onDispose(() => handle.unsubscribe());
      });
    });
  }
  queryCredentials({ spaceKey }) {
    return new Stream10(({ ctx, next }) => {
      var _a;
      const space = (_a = this._spaceManager.spaces.get(spaceKey)) != null ? _a : raise(new SpaceNotFoundError(spaceKey));
      const processor = space.spaceState.registerProcessor({
        process: async (credential) => {
          next(credential);
        }
      });
      ctx.onDispose(() => processor.close());
      scheduleTask4(ctx, () => processor.open());
    });
  }
  async writeCredentials({ spaceKey, credentials }) {
    var _a;
    const space = (_a = this._spaceManager.spaces.get(spaceKey)) != null ? _a : raise(new SpaceNotFoundError(spaceKey));
    for (const credential of credentials != null ? credentials : []) {
      await space.controlPipeline.writer.write({
        credential: {
          credential
        }
      });
    }
  }
  _transformSpace(space) {
    var _a, _b, _c, _d, _e;
    return {
      spaceKey: space.key,
      state: space.state,
      pipeline: {
        controlFeeds: space.inner.controlPipeline.state.feeds.map((feed) => feed.key),
        currentControlTimeframe: space.inner.controlPipeline.state.timeframe,
        targetControlTimeframe: space.inner.controlPipeline.state.targetTimeframe,
        totalControlTimeframe: space.inner.controlPipeline.state.endTimeframe,
        dataFeeds: (_b = (_a = space.dataPipeline.pipelineState) == null ? void 0 : _a.feeds.map((feed) => feed.key)) != null ? _b : [],
        currentDataTimeframe: (_c = space.dataPipeline.pipelineState) == null ? void 0 : _c.timeframe,
        targetDataTimeframe: (_d = space.dataPipeline.pipelineState) == null ? void 0 : _d.targetTimeframe,
        totalDataTimeframe: (_e = space.dataPipeline.pipelineState) == null ? void 0 : _e.endTimeframe
      },
      members: Array.from(space.inner.spaceState.members.values()).map((member) => {
        var _a2, _b2, _c2;
        return {
          identity: {
            identityKey: member.key,
            profile: {
              displayName: (_b2 = (_a2 = member.assertion.profile) == null ? void 0 : _a2.displayName) != null ? _b2 : humanize(member.key)
            }
          },
          presence: ((_c2 = this._identityManager.identity) == null ? void 0 : _c2.identityKey.equals(member.key)) || space.presence.getPeersOnline().filter(({ identityKey }) => identityKey.equals(member.key)).length > 0 ? SpaceMember.PresenceState.ONLINE : SpaceMember.PresenceState.OFFLINE
        };
      })
    };
  }
};
var getChannelId = (channel) => `user-channel/${channel}`;

// packages/sdk/client-services/src/packlets/system/system-service.ts
import { Stream as Stream11 } from "@dxos/codec-protobuf";
var SystemServiceImpl = class {
  constructor({ config, statusUpdate, onUpdateStatus, getCurrentStatus, onReset }) {
    this._config = config;
    this._statusUpdate = statusUpdate;
    this._getCurrentStatus = getCurrentStatus;
    this._onUpdateStatus = onUpdateStatus;
    this._onReset = onReset;
  }
  async getConfig() {
    var _a, _b;
    return (_b = (_a = this._config) == null ? void 0 : _a.values) != null ? _b : {};
  }
  async updateStatus({ status }) {
    await this._onUpdateStatus(status);
  }
  queryStatus() {
    return new Stream11(({ next }) => {
      const update = () => {
        next({
          status: this._getCurrentStatus()
        });
      };
      update();
      const unsubscribe = this._statusUpdate.on(() => update());
      const interval = setInterval(update, 3e3);
      return () => {
        clearInterval(interval);
        unsubscribe();
      };
    });
  }
  async reset() {
    await this._onReset();
  }
};

// packages/sdk/client-services/src/packlets/vault/iframe-proxy-runtime.ts
import { iframeServiceBundle, workerServiceBundle } from "@dxos/client";
import { WebRTCTransportService } from "@dxos/network-manager";
import { createProtoRpcPeer as createProtoRpcPeer2 } from "@dxos/rpc";
var IFrameProxyRuntime = class {
  constructor({ systemPort, workerAppPort, windowAppPort, shellPort }) {
    this._transportService = new WebRTCTransportService();
    this._systemPort = systemPort;
    this._windowAppPort = windowAppPort;
    this._workerAppPort = workerAppPort;
    this._shellPort = shellPort;
    this._systemRpc = createProtoRpcPeer2({
      requested: workerServiceBundle,
      exposed: iframeServiceBundle,
      handlers: {
        BridgeService: this._transportService,
        IframeService: {
          async heartbeat() {
          }
        }
      },
      port: this._systemPort,
      timeout: 200
    });
    this._workerAppPort.subscribe((msg) => this._windowAppPort.send(msg));
    this._windowAppPort.subscribe((msg) => this._workerAppPort.send(msg));
    if (this._shellPort) {
      this._shellRuntime = new ShellRuntimeImpl(this._shellPort);
    }
  }
  get shell() {
    return this._shellRuntime;
  }
  async open(origin) {
    var _a;
    await this._systemRpc.open();
    await this._systemRpc.rpc.WorkerService.start({
      origin
    });
    await ((_a = this._shellRuntime) == null ? void 0 : _a.open());
  }
  async close() {
    var _a;
    await ((_a = this._shellRuntime) == null ? void 0 : _a.close());
    await this._systemRpc.rpc.WorkerService.stop();
    await this._systemRpc.close();
  }
};

// packages/sdk/client-services/src/packlets/vault/vault-resource-lock.ts
import { asyncTimeout, Trigger as Trigger4 } from "@dxos/async";
import { log as log11, logInfo } from "@dxos/log";
var __decorate3 = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Message;
(function(Message2) {
  Message2["ACQUIRING"] = "acquiring";
})(Message || (Message = {}));
var VaultResourceLock = class {
  constructor({ lockKey, onAcquire, onRelease }) {
    this._broadcastChannel = new BroadcastChannel("vault-resource-lock");
    this._releaseTrigger = new Trigger4();
    this._lockKey = lockKey;
    this._onAcquire = onAcquire;
    this._onRelease = onRelease;
    this._broadcastChannel.onmessage = this._onMessage.bind(this);
  }
  get lockKey() {
    return this._lockKey;
  }
  async acquire() {
    this._broadcastChannel.postMessage({
      message: Message.ACQUIRING
    });
    try {
      log11("aquiring lock...", {}, {
        file: "vault-resource-lock.ts",
        line: 44,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await asyncTimeout(this._requestLock(), 3e3);
      log11("acquired lock", {}, {
        file: "vault-resource-lock.ts",
        line: 46,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } catch (e) {
      log11("stealing lock...", {}, {
        file: "vault-resource-lock.ts",
        line: 48,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await this._requestLock(true);
      log11("stolen lock", {}, {
        file: "vault-resource-lock.ts",
        line: 50,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  async release() {
    this._releaseTrigger.wake();
  }
  _onMessage(event) {
    if (event.data.message === Message.ACQUIRING) {
      this._releaseTrigger.wake();
    }
  }
  async _requestLock(steal = false) {
    log11("requesting lock...", {
      steal
    }, {
      file: "vault-resource-lock.ts",
      line: 65,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const acquired = new Trigger4();
    void navigator.locks.request(this._lockKey, {
      steal
    }, async () => {
      var _a, _b;
      await ((_a = this._onAcquire) == null ? void 0 : _a.call(this));
      acquired.wake();
      this._releaseTrigger = new Trigger4();
      await this._releaseTrigger.wait();
      log11("releasing lock...", {}, {
        file: "vault-resource-lock.ts",
        line: 74,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await ((_b = this._onRelease) == null ? void 0 : _b.call(this));
      log11("released lock", {}, {
        file: "vault-resource-lock.ts",
        line: 76,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }).catch(async () => {
      var _a;
      await ((_a = this._onRelease) == null ? void 0 : _a.call(this));
    });
    await acquired.wait();
    log11("recieved lock", {
      steal
    }, {
      file: "vault-resource-lock.ts",
      line: 83,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};
__decorate3([
  logInfo
], VaultResourceLock.prototype, "lockKey", null);

// packages/sdk/client-services/src/packlets/vault/worker-runtime.ts
import { Trigger as Trigger6 } from "@dxos/async";
import { log as log13 } from "@dxos/log";
import { MemorySignalManager, MemorySignalManagerContext, WebsocketSignalManager } from "@dxos/messaging";
import { NetworkManager, WebRTCTransportProxyFactory } from "@dxos/network-manager";

// packages/sdk/client-services/src/packlets/vault/worker-session.ts
import assert11 from "@dxos/node-std/assert";
import { asyncTimeout as asyncTimeout2, Trigger as Trigger5 } from "@dxos/async";
import { iframeServiceBundle as iframeServiceBundle2, workerServiceBundle as workerServiceBundle2 } from "@dxos/client";
import { log as log12, logInfo as logInfo2 } from "@dxos/log";
import { createProtoRpcPeer as createProtoRpcPeer3 } from "@dxos/rpc";
import { Callback } from "@dxos/util";

// packages/sdk/client-services/src/packlets/services/client-rpc-server.ts
import { Stream as Stream12 } from "@dxos/codec-protobuf";
import { raise as raise2 } from "@dxos/debug";
import { parseMethodName, RpcPeer } from "@dxos/rpc";
var ClientRpcServer = class {
  constructor(params) {
    this._handlerCache = /* @__PURE__ */ new Map();
    const { serviceRegistry, handleCall, handleStream, ...rpcOptions } = params;
    this._handleCall = handleCall;
    this._handleStream = handleStream;
    this._serviceRegistry = serviceRegistry;
    this._rpcPeer = new RpcPeer({
      ...rpcOptions,
      callHandler: (method, params2) => {
        const [serviceName, methodName] = parseMethodName(method);
        const handler = (method2, params3) => this._getServiceHandler(serviceName).call(method2, params3);
        if (this._handleCall) {
          return this._handleCall(methodName, params2, handler);
        } else {
          return handler(methodName, params2);
        }
      },
      streamHandler: (method, params2) => {
        const [serviceName, methodName] = parseMethodName(method);
        const handler = (method2, params3) => this._getServiceHandler(serviceName).callStream(method2, params3);
        if (this._handleStream) {
          return Stream12.unwrapPromise(this._handleStream(methodName, params2, handler));
        } else {
          return handler(methodName, params2);
        }
      }
    });
  }
  async open() {
    await this._rpcPeer.open();
  }
  async close() {
    await this._rpcPeer.close();
  }
  _getServiceHandler(serviceName) {
    var _a;
    if (!this._handlerCache.has(serviceName)) {
      const [key, descriptor] = (_a = Object.entries(this._serviceRegistry.descriptors).find(([key2, descriptor2]) => descriptor2.name === serviceName)) != null ? _a : raise2(new Error(`Service not available: ${serviceName}`));
      const service = this._serviceRegistry.services[key];
      if (!service) {
        throw new Error(`Service not available: ${serviceName}`);
      }
      this._handlerCache.set(serviceName, descriptor.createServer(service));
    }
    return this._handlerCache.get(serviceName);
  }
};

// packages/sdk/client-services/src/packlets/vault/worker-session.ts
var __decorate4 = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var WorkerSession = class {
  constructor({ serviceHost, systemPort, appPort, shellPort, readySignal, options = {
    heartbeatInterval: 1e3
  } }) {
    this._startTrigger = new Trigger5();
    this.onClose = new Callback();
    assert11(options);
    assert11(serviceHost);
    this._options = options;
    this._serviceHost = serviceHost;
    const middleware = {
      handleCall: async (method, params, handler) => {
        const error = await readySignal.wait({
          timeout: 3e3
        });
        if (error) {
          throw error;
        }
        return handler(method, params);
      },
      handleStream: async (method, params, handler) => {
        const error = await readySignal.wait({
          timeout: 3e3
        });
        if (error) {
          throw error;
        }
        return handler(method, params);
      }
    };
    this._clientRpc = new ClientRpcServer({
      serviceRegistry: this._serviceHost.serviceRegistry,
      port: appPort,
      ...middleware
    });
    this._shellClientRpc = new ClientRpcServer({
      serviceRegistry: this._serviceHost.serviceRegistry,
      port: shellPort,
      ...middleware
    });
    this._iframeRpc = createProtoRpcPeer3({
      requested: iframeServiceBundle2,
      exposed: workerServiceBundle2,
      handlers: {
        WorkerService: {
          start: async (request) => {
            this.origin = request.origin;
            this._startTrigger.wake();
          },
          stop: async () => {
            setTimeout(async () => {
              try {
                await this.close();
              } catch (err) {
                log12.catch(err, {}, {
                  file: "worker-session.ts",
                  line: 107,
                  scope: this,
                  callSite: (f, a) => f(...a)
                });
              }
            });
          }
        }
      },
      port: systemPort,
      timeout: 1e3
      // With low timeout heartbeat may fail if the tab's thread is saturated.
    });
    this.bridgeService = this._iframeRpc.rpc.BridgeService;
  }
  async open() {
    log12.info("opening..", {}, {
      file: "worker-session.ts",
      line: 121,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await Promise.all([
      this._clientRpc.open(),
      this._iframeRpc.open(),
      this._maybeOpenShell()
    ]);
    await this._startTrigger.wait({
      timeout: 3e3
    });
    this._heartbeatTimer = setInterval(async () => {
      try {
        await this._iframeRpc.rpc.IframeService.heartbeat();
      } catch (err1) {
        log12.warn("Heartbeat failed", {
          err: err1
        }, {
          file: "worker-session.ts",
          line: 133,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        try {
          await this.close();
        } catch (err) {
          log12.catch(err, {}, {
            file: "worker-session.ts",
            line: 137,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
      }
    }, this._options.heartbeatInterval);
  }
  async close() {
    log12.info("closing..", {}, {
      file: "worker-session.ts",
      line: 144,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    try {
      await this.onClose.callIfSet();
    } catch (err) {
      log12.catch(err, {}, {
        file: "worker-session.ts",
        line: 148,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
    if (this._heartbeatTimer !== void 0) {
      clearInterval(this._heartbeatTimer);
    }
    await Promise.all([
      this._clientRpc.close(),
      this._iframeRpc.close()
    ]);
  }
  async _maybeOpenShell() {
    try {
      await asyncTimeout2(this._shellClientRpc.open(), 1e3);
    } catch (e) {
      log12.info("No shell connected.", {}, {
        file: "worker-session.ts",
        line: 162,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
};
__decorate4([
  logInfo2
], WorkerSession.prototype, "origin", void 0);

// packages/sdk/client-services/src/packlets/vault/worker-runtime.ts
var WorkerRuntime = class {
  // prettier-ignore
  constructor(_configProvider) {
    this._configProvider = _configProvider;
    this._transportFactory = new WebRTCTransportProxyFactory();
    this._ready = new Trigger6();
    this.sessions = /* @__PURE__ */ new Set();
    this._clientServices = new ClientServicesHost();
  }
  get host() {
    return this._clientServices;
  }
  async start() {
    log13("starting...", {}, {
      file: "worker-runtime.ts",
      line: 48,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    try {
      this._config = await this._configProvider();
      const signalServer = this._config.get("runtime.services.signal.server");
      this._clientServices.initialize({
        config: this._config,
        networkManager: new NetworkManager({
          log: true,
          signalManager: signalServer ? new WebsocketSignalManager([
            signalServer
          ]) : new MemorySignalManager(new MemorySignalManagerContext()),
          transportFactory: this._transportFactory
        })
      });
      await this._clientServices.open();
      this._ready.wake(void 0);
      log13("started", {}, {
        file: "worker-runtime.ts",
        line: 65,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } catch (err) {
      this._ready.wake(err);
      log13.catch(err, {}, {
        file: "worker-runtime.ts",
        line: 68,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  async stop() {
    await this._clientServices.close();
  }
  /**
  * Create a new session.
  */
  async createSession({ appPort, systemPort, shellPort }) {
    const session = new WorkerSession({
      serviceHost: this._clientServices,
      appPort,
      systemPort,
      shellPort,
      readySignal: this._ready
    });
    session.onClose.set(async () => {
      this.sessions.delete(session);
      this._reconnectWebrtc();
    });
    await session.open();
    this.sessions.add(session);
    this._reconnectWebrtc();
  }
  /**
  * Selects one of the existing session fro WebRTC networking.
  */
  _reconnectWebrtc() {
    if (this._sessionForNetworking) {
      if (!this.sessions.has(this._sessionForNetworking)) {
        this._sessionForNetworking = void 0;
      }
    }
    if (!this._sessionForNetworking) {
      const selected = Array.from(this.sessions).find((session) => session.bridgeService);
      if (selected) {
        this._sessionForNetworking = selected;
        this._transportFactory.setBridgeService(selected.bridgeService);
      } else {
        this._transportFactory.setBridgeService(void 0);
      }
    }
  }
};

// packages/sdk/client-services/src/packlets/services/service-context.ts
import assert12 from "@dxos/node-std/assert";
import { Trigger as Trigger7 } from "@dxos/async";
import { Invitation as Invitation5 } from "@dxos/client";
import { getCredentialAssertion as getCredentialAssertion3 } from "@dxos/credentials";
import { failUndefined as failUndefined2 } from "@dxos/debug";
import { valueEncoding, MetadataStore, SpaceManager, DataServiceSubscriptions, SnapshotStore } from "@dxos/echo-pipeline";
import { FeedFactory, FeedStore } from "@dxos/feed-store";
import { Keyring } from "@dxos/keyring";
import { PublicKey as PublicKey8 } from "@dxos/keys";
import { log as log14 } from "@dxos/log";
import { trace as trace3 } from "@dxos/protocols";
var ServiceContext = class {
  // prettier-ignore
  constructor(storage, networkManager, modelFactory) {
    this.storage = storage;
    this.networkManager = networkManager;
    this.modelFactory = modelFactory;
    this.initialized = new Trigger7();
    this.dataServiceSubscriptions = new DataServiceSubscriptions();
    this._handlerFactories = /* @__PURE__ */ new Map();
    this._instanceId = PublicKey8.random().toHex();
    networkManager._traceParent = this._instanceId;
    this.metadataStore = new MetadataStore(storage.createDirectory("metadata"));
    this.snapshotStore = new SnapshotStore(storage.createDirectory("snapshots"));
    this.keyring = new Keyring(storage.createDirectory("keyring"));
    this.feedStore = new FeedStore({
      factory: new FeedFactory({
        root: storage.createDirectory("feeds"),
        signer: this.keyring,
        hypercore: {
          valueEncoding
        }
      })
    });
    this.spaceManager = new SpaceManager({
      feedStore: this.feedStore,
      networkManager: this.networkManager
    });
    this.spaceManager._traceParent = this._instanceId;
    this.identityManager = new IdentityManager(this.metadataStore, this.keyring, this.feedStore, this.spaceManager);
    this.identityManager._traceParent = this._instanceId;
    this.invitations = new InvitationsHandler(this.networkManager);
    this._handlerFactories.set(Invitation5.Kind.DEVICE, () => new DeviceInvitationProtocol(this.keyring, () => {
      var _a;
      return (_a = this.identityManager.identity) != null ? _a : failUndefined2();
    }, this._acceptIdentity.bind(this)));
  }
  async open() {
    log14.trace("dxos.sdk.service-context", trace3.begin({
      id: this._instanceId
    }), {
      file: "service-context.ts",
      line: 114,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    log14("opening...", {}, {
      file: "service-context.ts",
      line: 116,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this.networkManager.open();
    await this.spaceManager.open();
    await this.identityManager.open();
    if (this.identityManager.identity) {
      await this._initialize();
    }
    log14("opened", {}, {
      file: "service-context.ts",
      line: 123,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  async close() {
    var _a, _b;
    log14("closing...", {}, {
      file: "service-context.ts",
      line: 127,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await ((_a = this._deviceSpaceSync) == null ? void 0 : _a.close());
    await ((_b = this.dataSpaceManager) == null ? void 0 : _b.close());
    await this.identityManager.close();
    await this.spaceManager.close();
    await this.feedStore.close();
    await this.networkManager.close();
    this.dataServiceSubscriptions.clear();
    log14("closed", {}, {
      file: "service-context.ts",
      line: 135,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    log14.trace("dxos.sdk.service-context", trace3.end({
      id: this._instanceId
    }), {
      file: "service-context.ts",
      line: 137,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  async reset() {
    log14("resetting...", {}, {
      file: "service-context.ts",
      line: 141,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this.close();
    await this.storage.reset();
    log14("reset", {}, {
      file: "service-context.ts",
      line: 144,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  async createIdentity(params = {}) {
    const identity = await this.identityManager.createIdentity(params);
    await this._initialize();
    return identity;
  }
  getInvitationHandler(invitation) {
    const factory = this._handlerFactories.get(invitation.kind);
    assert12(factory, `Unknown invitation kind: ${invitation.kind}`);
    return factory(invitation);
  }
  async _acceptIdentity(params) {
    const identity = await this.identityManager.acceptIdentity(params);
    await this._initialize();
    return identity;
  }
  // Called when identity is created.
  async _initialize() {
    var _a;
    log14("initializing spaces...", {}, {
      file: "service-context.ts",
      line: 169,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const identity = (_a = this.identityManager.identity) != null ? _a : failUndefined2();
    const signingContext = {
      credentialSigner: identity.getIdentityCredentialSigner(),
      identityKey: identity.identityKey,
      deviceKey: identity.deviceKey,
      profile: identity.profileDocument,
      recordCredential: async (credential) => {
        await identity.controlPipeline.writer.write({
          credential: {
            credential
          }
        });
      }
    };
    this.dataSpaceManager = new DataSpaceManager(this.spaceManager, this.metadataStore, this.dataServiceSubscriptions, this.keyring, signingContext, this.modelFactory, this.feedStore, this.snapshotStore);
    this.dataSpaceManager._traceParent = this._instanceId;
    await this.dataSpaceManager.open();
    this._handlerFactories.set(Invitation5.Kind.SPACE, (invitation) => {
      assert12(this.dataSpaceManager, "dataSpaceManager not initialized yet");
      return new SpaceInvitationProtocol(this.dataSpaceManager, signingContext, this.keyring, invitation.spaceKey);
    });
    this.initialized.wake();
    this._deviceSpaceSync = identity.space.spaceState.registerProcessor({
      process: async (credential) => {
        const assertion = getCredentialAssertion3(credential);
        if (assertion["@type"] !== "dxos.halo.credentials.SpaceMember") {
          return;
        }
        if (assertion.spaceKey.equals(identity.space.key)) {
          return;
        }
        if (!this.dataSpaceManager) {
          log14("dataSpaceManager not initialized yet, ignoring space admission", {
            details: assertion
          }, {
            file: "service-context.ts",
            line: 211,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return;
        }
        if (this.dataSpaceManager.spaces.has(assertion.spaceKey)) {
          log14("space already exists, ignoring space admission", {
            details: assertion
          }, {
            file: "service-context.ts",
            line: 215,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return;
        }
        try {
          log14("accepting space recorded in halo", {
            details: assertion
          }, {
            file: "service-context.ts",
            line: 220,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          await this.dataSpaceManager.acceptSpace({
            spaceKey: assertion.spaceKey,
            genesisFeedKey: assertion.genesisFeedKey
          });
        } catch (err) {
          log14.catch(err, {}, {
            file: "service-context.ts",
            line: 226,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
      }
    });
    await this._deviceSpaceSync.open();
  }
};

// packages/sdk/client-services/src/packlets/services/service-registry.ts
var ServiceRegistry = class {
  // prettier-ignore
  constructor(_serviceBundle, _handlers = {}) {
    this._serviceBundle = _serviceBundle;
    this._handlers = _handlers;
  }
  get descriptors() {
    return this._serviceBundle;
  }
  get services() {
    return this._handlers;
  }
  setServices(services) {
    this._handlers = services;
  }
  addService(name, service) {
    this._handlers[name] = service;
  }
  removeService(name) {
    delete this._handlers[name];
  }
};

// packages/sdk/client-services/src/packlets/services/service-host.ts
var ClientServicesHost = class {
  constructor({
    config,
    modelFactory = createDefaultModelFactory(),
    // TODO(burdon): Create ApolloLink abstraction (see Client).
    networkManager,
    storage,
    // TODO(wittjosiah): Turn this on by default.
    lockKey
  } = {}) {
    this._statusUpdate = new Event7();
    this._opening = false;
    this._open = false;
    this._instanceId = PublicKey9.random().toHex();
    this._storage = storage;
    this._modelFactory = modelFactory;
    if (config) {
      this.initialize({
        config,
        networkManager
      });
    }
    this._resourceLock = lockKey ? new VaultResourceLock({
      lockKey,
      onAcquire: () => {
        this._opening || this.open();
      },
      onRelease: () => this.close()
    }) : void 0;
    this._systemService = new SystemServiceImpl({
      config: this._config,
      statusUpdate: this._statusUpdate,
      getCurrentStatus: () => this.isOpen ? SystemStatus.ACTIVE : SystemStatus.INACTIVE,
      onUpdateStatus: async (status) => {
        var _a, _b;
        if (!this.isOpen && status === SystemStatus.ACTIVE) {
          await ((_a = this._resourceLock) == null ? void 0 : _a.acquire());
        } else if (this.isOpen && status === SystemStatus.INACTIVE) {
          await ((_b = this._resourceLock) == null ? void 0 : _b.release());
        }
      },
      onReset: async () => {
        assert13(this._serviceContext, "service host is closed");
        await this._serviceContext.reset();
      }
    });
    this._serviceRegistry = new ServiceRegistry(clientServiceBundle, {
      SystemService: this._systemService
    });
  }
  get isOpen() {
    return this._open;
  }
  get serviceRegistry() {
    return this._serviceRegistry;
  }
  get descriptors() {
    return this._serviceRegistry.descriptors;
  }
  get services() {
    return this._serviceRegistry.services;
  }
  /**
  * Initialize the service host with the config.
  * Config can also be provided in the constructor.
  * Can only be called once.
  */
  initialize({ config, networkManager }) {
    assert13(!this._open, "service host is open");
    if (config) {
      assert13(!this._config, "config already set");
      this._config = config;
      if (!this._storage) {
        this._storage = createStorageObjects(config.get("runtime.client.storage", {})).storage;
      }
    }
    if (networkManager) {
      assert13(!this._networkManager, "network manager already set");
      this._networkManager = networkManager;
    }
  }
  async open() {
    var _a, _b, _c;
    if (this._open) {
      return;
    }
    log15.trace("dxos.sdk.client-services-host", trace4.begin({
      id: this._instanceId
    }), {
      file: "service-host.ts",
      line: 167,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert13(this._config, "config not set");
    assert13(this._storage, "storage not set");
    assert13(this._networkManager, "network manager not set");
    this._opening = true;
    log15("opening...", {
      lockKey: (_a = this._resourceLock) == null ? void 0 : _a.lockKey
    }, {
      file: "service-host.ts",
      line: 174,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await ((_b = this._resourceLock) == null ? void 0 : _b.acquire());
    this._serviceContext = new ServiceContext(this._storage, this._networkManager, this._modelFactory);
    this._serviceRegistry.setServices({
      SystemService: this._systemService,
      IdentityService: new IdentityServiceImpl(this._serviceContext),
      InvitationsService: new InvitationsServiceImpl(this._serviceContext.invitations, (invitation) => this._serviceContext.getInvitationHandler(invitation)),
      DevicesService: new DevicesServiceImpl(this._serviceContext.identityManager),
      SpacesService: new SpacesServiceImpl(this._serviceContext.identityManager, this._serviceContext.spaceManager, this._serviceContext.dataServiceSubscriptions, async () => {
        await this._serviceContext.initialized.wait();
        return this._serviceContext.dataSpaceManager;
      }),
      DataService: new DataServiceImpl(this._serviceContext.dataServiceSubscriptions),
      NetworkService: new NetworkServiceImpl(this._serviceContext.networkManager),
      // TODO(burdon): Move to new protobuf definitions.
      TracingService: new TracingServiceImpl(this._config),
      DevtoolsHost: new DevtoolsServiceImpl({
        events: new DevtoolsHostEvents(),
        config: this._config,
        context: this._serviceContext
      })
    });
    await this._serviceContext.open();
    this._opening = false;
    this._open = true;
    this._statusUpdate.emit();
    const deviceKey = (_c = this._serviceContext.identityManager.identity) == null ? void 0 : _c.deviceKey;
    log15("opened", {
      deviceKey
    }, {
      file: "service-host.ts",
      line: 221,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  async close() {
    var _a;
    if (!this._open) {
      return;
    }
    const deviceKey = (_a = this._serviceContext.identityManager.identity) == null ? void 0 : _a.deviceKey;
    log15("closing...", {
      deviceKey
    }, {
      file: "service-host.ts",
      line: 230,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._serviceRegistry.setServices({
      SystemService: this._systemService
    });
    await this._serviceContext.close();
    this._open = false;
    this._statusUpdate.emit();
    log15("closed", {
      deviceKey
    }, {
      file: "service-host.ts",
      line: 235,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    log15.trace("dxos.sdk.client-services-host", trace4.end({
      id: this._instanceId
    }), {
      file: "service-host.ts",
      line: 237,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};

// packages/sdk/client-services/src/packlets/services/local-client-services.ts
var LocalClientServices = class {
  constructor(params) {
    this._host = new ClientServicesHost(params);
  }
  get descriptors() {
    return this._host.descriptors;
  }
  get services() {
    return this._host.services;
  }
  get host() {
    return this._host;
  }
  async open() {
    await this._host.open();
  }
  async close() {
    await this._host.close();
  }
};

// packages/sdk/client-services/src/packlets/services/utils.ts
import { Config } from "@dxos/config";
import { log as log16 } from "@dxos/log";
import { MemorySignalManager as MemorySignalManager2, MemorySignalManagerContext as MemorySignalManagerContext2, WebsocketSignalManager as WebsocketSignalManager2 } from "@dxos/messaging";
import { createWebRTCTransportFactory, MemoryTransportFactory, NetworkManager as NetworkManager2 } from "@dxos/network-manager";
var fromHost = (config = new Config()) => {
  return new LocalClientServices({
    config,
    networkManager: createNetworkManager(config)
  });
};
var createNetworkManager = (config, options = {}) => {
  const signalServer = config.get("runtime.services.signal.server");
  if (signalServer) {
    const { log: log1 = true, signalManager = new WebsocketSignalManager2([
      signalServer
    ]), transportFactory = createWebRTCTransportFactory({
      iceServers: config.get("runtime.services.ice")
    }) } = options;
    return new NetworkManager2({
      log: log1,
      signalManager,
      transportFactory
    });
  }
  log16.warn("P2P network is not configured.", {}, {
    file: "utils.ts",
    line: 48,
    scope: void 0,
    callSite: (f, a) => f(...a)
  });
  return new NetworkManager2({
    signalManager: new MemorySignalManager2(new MemorySignalManagerContext2()),
    transportFactory: MemoryTransportFactory
  });
};

// packages/sdk/client-services/src/packlets/vault/iframe-host-runtime.ts
var __decorate5 = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var LOCK_KEY = "DXOS_RESOURCE_LOCK";
var IFrameHostRuntime = class {
  constructor({ configProvider, appPort, shellPort }) {
    this._transportFactory = createWebRTCTransportFactory2();
    this._ready = new Trigger8();
    this._configProvider = configProvider;
    this._appPort = appPort;
    this._shellPort = shellPort;
    if (this._shellPort) {
      this._shellRuntime = new ShellRuntimeImpl(this._shellPort);
    }
  }
  get services() {
    return this._clientServices;
  }
  get shell() {
    return this._shellRuntime;
  }
  async start() {
    var _a;
    log17("starting...", {}, {
      file: "iframe-host-runtime.ts",
      line: 67,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    try {
      this._config = await getAsyncValue(this._configProvider);
      const signalServer = this._config.get("runtime.services.signal.server");
      this._clientServices = new LocalClientServices({
        lockKey: LOCK_KEY,
        config: this._config,
        networkManager: new NetworkManager3({
          log: true,
          signalManager: signalServer ? new WebsocketSignalManager3([
            signalServer
          ]) : new MemorySignalManager3(new MemorySignalManagerContext3()),
          transportFactory: this._transportFactory
        })
      });
      const middleware = {
        handleCall: async (method, params, handler) => {
          const error = await this._ready.wait({
            timeout: 3e3
          });
          if (error) {
            throw error;
          }
          return handler(method, params);
        },
        handleStream: async (method, params, handler) => {
          const error = await this._ready.wait({
            timeout: 3e3
          });
          if (error) {
            throw error;
          }
          return handler(method, params);
        }
      };
      this._clientRpc = new ClientRpcServer({
        serviceRegistry: this._clientServices.host.serviceRegistry,
        port: this._appPort,
        ...middleware
      });
      await Promise.all([
        this._clientServices.open(),
        this._clientRpc.open(),
        (_a = this._shellRuntime) == null ? void 0 : _a.open()
      ]);
      this._ready.wake(void 0);
      log17("started", {}, {
        file: "iframe-host-runtime.ts",
        line: 110,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } catch (err) {
      this._ready.wake(err);
      log17.catch(err, {}, {
        file: "iframe-host-runtime.ts",
        line: 113,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  async stop() {
    var _a;
    log17("stopping...", {}, {
      file: "iframe-host-runtime.ts",
      line: 118,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._clientRpc.close();
    await this._clientServices.close();
    await ((_a = this._shellRuntime) == null ? void 0 : _a.close());
    log17("stopped", {}, {
      file: "iframe-host-runtime.ts",
      line: 122,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};
__decorate5([
  logInfo3
], IFrameHostRuntime.prototype, "origin", void 0);

export {
  subscribeToFeeds,
  subscribeToFeedBlocks,
  subscribeToNetworkStatus,
  subscribeToSignal,
  subscribeToNetworkTopics,
  subscribeToSwarmInfo,
  getNetworkPeers,
  subscribeToSpaces,
  DevtoolsHostEvents,
  DevtoolsServiceImpl,
  createAuthProvider,
  TrustedKeySetAuthVerifier,
  Identity,
  IdentityManager,
  IdentityServiceImpl,
  DeviceInvitationProtocol,
  InvitationsHandler,
  InvitationsServiceImpl,
  SpaceInvitationProtocol,
  createStorageObjects,
  ShellRuntimeImpl,
  MemoryShellRuntime,
  IFrameHostRuntime,
  IFrameProxyRuntime,
  VaultResourceLock,
  WorkerSession,
  WorkerRuntime,
  ServiceContext,
  ServiceRegistry,
  ClientServicesHost,
  LocalClientServices,
  fromHost
};
//# sourceMappingURL=chunk-2AYYVPWN.mjs.map
